// plan-modusign-style-acceptance §Phase1 — 서명 양식 디자이너 페이지.
// 3-패널 레이아웃:
//   Left   — 서명자 슬롯 팔레트 + 필드 타입 팔레트
//   Center — PDF 뷰어 (iframe) + 필드 오버레이 (클릭 배치 / 드래그 이동)
//   Right  — 선택된 필드 속성 편집

import {
  Fragment,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  type CSSProperties,
  type ReactNode,
  type ChangeEvent,
} from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  isLayoutGeneratedField,
  layoutAutoFields,
  layoutImageKeys,
  layoutDefaultPages,
  layoutDeletePage,
  layoutHitCell,
  layoutInsertBlankPage,
  layoutPageCount,
  validateTemplateLayout,
  LAYOUT_OBJECT_LABELS,
  type SignatureField,
  type SignerRole,
  type FieldType,
  type DataBinding,
  type LayoutImageRef,
  type LayoutObject,
  type LayoutObjectKind,
  type TemplateLayout,
  type UpdateSignatureTemplateResponseDto,
} from '@bhops/shared';
import { signatureTemplatesApi } from '@/api/signatureTemplates';
import { signatureLayoutAssetsApi } from '@/api/signatureLayoutAssets';
import { extractApiError } from '@/api/client';
import { Button } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { useToast } from '@/components/Toast';
import * as pdfjsLib from 'pdfjs-dist';
import { PdfCanvasViewer } from './PdfCanvasViewer';
import {
  fitImageBox,
  mastersInRange,
  useLayoutEditor,
  type LayoutChangeMeta,
  type PickedImage,
} from './layout/useLayoutEditor';
import { LayoutObjectView } from './layout/LayoutObjectView';
import { LayoutPropertyPanel } from './layout/LayoutPropertyPanel';
import {
  ASSET_DRAG_MIME,
  LAYOUT_ASSETS_QUERY_KEY,
  LayoutAssetLibrary,
  type AssetDragPayload,
} from './layout/LayoutAssetLibrary';
import { LayoutPresetDialog } from './layout/LayoutPresetDialog';
import { useCellArrowKeys } from './layout/useCellArrowKeys';
import { useCellClipboard } from './layout/useCellClipboard';

// ── Constants ──────────────────────────────────────────────────────────────

/** A4 display width in px (595pt × 96/72 ≈ 794) */
const PAGE_W = 794;
/** A4 display height in px per page (842pt × 96/72 ≈ 1122) */
const PAGE_H = 1122;

const FIELD_TYPE_OPTIONS: {
  type: FieldType;
  label: string;
  defaultW: number;
  defaultH: number;
}[] = [
  { type: 'text', label: '텍스트', defaultW: 180, defaultH: 32 },
  { type: 'signature', label: '서명', defaultW: 200, defaultH: 80 },
  { type: 'stamp', label: '도장', defaultW: 80, defaultH: 80 },
  { type: 'date', label: '날짜', defaultW: 140, defaultH: 32 },
  { type: 'checkbox', label: '체크박스', defaultW: 24, defaultH: 24 },
  { type: 'dropdown', label: '드롭다운', defaultW: 180, defaultH: 32 },
  { type: 'image', label: '이미지', defaultW: 120, defaultH: 80 },
];

// acceptance-ux-changes-2026-06 §A5 / D4 — 타입별 최소 크기(px).
// 기존 전역 10px 고정 클램프가 "텍스트 박스가 더 안 줄어 겹친다"는 문제의 원인.
// text/date/dropdown 은 8px 까지 축소 허용(겹침 회피용), 서명/도장 등은 형태 유지를
// 위해 더 큰 하한 유지. (PDF 폰트 하한 6pt — 너무 작으면 글자가 안 보일 수 있음)
const MIN_SIZE_BY_TYPE: Record<FieldType, { w: number; h: number }> = {
  text: { w: 8, h: 8 },
  date: { w: 8, h: 8 },
  dropdown: { w: 8, h: 8 },
  checkbox: { w: 12, h: 12 },
  signature: { w: 24, h: 16 },
  stamp: { w: 16, h: 16 },
  image: { w: 12, h: 12 },
};
const minW = (t: FieldType): number => MIN_SIZE_BY_TYPE[t]?.w ?? 8;
const minH = (t: FieldType): number => MIN_SIZE_BY_TYPE[t]?.h ?? 8;

// acceptance-ux-changes-2026-06 §A6 / D2 — 필드 겹침 감지(경고만, 자동 보정 X).
// 좌표는 문서 전체 절대값(y 에 page offset 포함)이라 AABB 만으로 페이지 구분됨.
function fieldsOverlap(a: SignatureField, b: SignatureField): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
function findOverlappingFieldIds(fields: SignatureField[]): Set<string> {
  const ids = new Set<string>();
  for (let i = 0; i < fields.length; i += 1) {
    for (let j = i + 1; j < fields.length; j += 1) {
      if (fieldsOverlap(fields[i], fields[j])) {
        ids.add(fields[i].id);
        ids.add(fields[j].id);
      }
    }
  }
  return ids;
}

// 서명자 구별용 범주형 팔레트 — 의미 토큰 없는 의도적 distinct hue(규칙2 예외)
const SIGNER_COLORS = ['#4A90D9', '#E8553E', '#27AE60', '#9B59B6', '#F39C12', '#1ABC9C'];

// F1 — Coalesce RightPanel property edits within this window into a single
// history entry so typing "180" into a number input (3 keystrokes) becomes
// 1 undoable action instead of 3.
const EDIT_COALESCE_MS = 400;

// ── Helpers ────────────────────────────────────────────────────────────────

// round5 #6a — 타입별 '마지막 사용 크기' 기억(세션 한정). 배치가 정적 상수만
// 참조해 같은 크기 필드를 여러 개 만들 때마다 리사이즈를 반복하던 불편 해소.
// 타입별로 기억해야 체크박스(소형)와 서명(대형)이 섞이지 않는다. 렌더와
// 무관한 UX 메모리라 모듈 스코프 mutable 로 충분(리렌더 불필요).
const lastSizeByType: Partial<Record<FieldType, { w: number; h: number }>> = {};

// round5 #6b/#6d — 위치/크기 숫자 입력 공용 컴포넌트.
// #6b: (구) onChange 즉시 clamp(Math.max(10, …))가 '55 입력 → 105' 버그의
//      원인 — 전체 선택 후 '5' 입력이 즉시 10 으로 강제된 뒤 '5' 가 이어붙음.
//      포커스 중엔 raw 문자열을 들고 blur/Enter 에만 parse+clamp+커밋,
//      Escape 는 원복.
// #6d: 좌표계는 전 스택 top-left 일관(저장 포맷 무변경) — Y 의 체감 반전은
//      네이티브 스피너의 '▼=값 감소=위로 이동' 의미 충돌. type="text" 로
//      스피너를 없애고 ArrowUp/Down 을 invertArrows 로 시각 방향과 정합
//      (Y: ▼/ArrowDown = 아래로 = y+1).
function NumberField({
  label,
  value,
  min,
  invertArrows,
  onCommit,
}: {
  label: string;
  value: number;
  min?: number;
  invertArrows?: boolean;
  onCommit: (v: number) => void;
}) {
  const [raw, setRaw] = useState(String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setRaw(String(value));
  }, [value, focused]);

  const clamp = (n: number) => (min !== undefined ? Math.max(min, Math.round(n)) : Math.round(n));
  const commit = () => {
    const n = Number(raw);
    if (Number.isFinite(n) && raw.trim() !== '') onCommit(clamp(n));
    else setRaw(String(value));
  };
  const step = (arrow: 'up' | 'down') => {
    const delta = invertArrows ? (arrow === 'up' ? -1 : 1) : arrow === 'up' ? 1 : -1;
    const base = focused && raw.trim() !== '' && Number.isFinite(Number(raw)) ? Number(raw) : value;
    const next = clamp(base + delta);
    setRaw(String(next));
    onCommit(next);
  };

  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 2 }}>{label}</div>
      <div style={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
        <input
          type="text"
          inputMode="numeric"
          value={raw}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            commit();
          }}
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commit();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              setRaw(String(value));
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              step('up');
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              step('down');
            }
          }}
          style={{ ...inputSt, width: '100%', boxSizing: 'border-box' }}
          aria-label={label}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <button
            type="button"
            onClick={() => step('up')}
            style={stepperBtn}
            aria-label={`${label} 증가`}
          >
            <Icon name="chev-up" size={8} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => step('down')}
            style={stepperBtn}
            aria-label={`${label} 감소`}
          >
            <Icon name="chev-down" size={8} aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}

const stepperBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  padding: '2px 4px',
  border: '1px solid var(--border-default)',
  borderRadius: 2,
  background: 'var(--bg-surface)',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
};

function makeField(
  type: FieldType,
  x: number,
  y: number,
  defaultW: number,
  defaultH: number,
  signerSlot: string | null,
): SignatureField {
  return {
    id: crypto.randomUUID(),
    pageIndex: Math.floor(y / PAGE_H),
    x: Math.max(0, Math.round(x)),
    y: Math.max(0, Math.round(y)),
    w: defaultW,
    h: defaultH,
    type,
    signerSlot,
    binding: { kind: 'literal' },
    required: type === 'signature',
    defaultValue: null,
    placeholder: null,
    // 새 필드는 가운데 정렬(오너 요청 2026-09-23). 명시해 두어야 포털·현장앱·서버 stamp 가
    // 같은 자리에 찍는다 — 그쪽은 align 이 없으면 왼쪽이다(옛 필드 역호환).
    align: 'center',
  };
}

/**
 * 복제(Ctrl+D) / 붙여넣기(Ctrl+V) 공용 — 원본에서 (20·n, 20·n) 만큼 어긋난
 * 새 필드를 만든다. `n` 은 연속 붙여넣기 횟수라 계단식으로 쌓인다.
 *
 * 페이지 밖으로 나가지 않게 clamp 하고, y 가 바뀌었으므로 pageIndex 를
 * 다시 계산한다.
 */
function offsetDuplicate(src: SignatureField, totalH: number, n: number): SignatureField {
  const d = 20 * n;
  const nx = Math.max(0, Math.min(PAGE_W - src.w, src.x + d));
  const ny = Math.max(0, Math.min(totalH - src.h, src.y + d));
  return {
    ...src,
    id: crypto.randomUUID(),
    x: nx,
    y: ny,
    pageIndex: Math.floor(ny / PAGE_H),
  };
}

function resolveFieldColor(field: SignatureField, roles: SignerRole[]): string {
  const role = roles.find((r) => r.slot === field.signerSlot);
  return role?.color ?? 'var(--fg-subtle)';
}

/**
 * Best-effort PDF page-count detection without pdfjs-dist.
 *
 * PDFs declare each page as `/Type /Page` (singular — `/Pages` is the tree
 * root). For uncompressed PDFs this regex is reliable. For PDFs with
 * compressed object streams (XRef streams), the markers may be inside
 * deflated blobs and the count returns 0; caller falls back to manual input.
 *
 * Returns 0 if detection failed — caller should prompt the operator.
 */
async function detectPdfPageCount(file: File): Promise<number> {
  try {
    const buf = await file.arrayBuffer();
    // Decode as Latin-1 — PDF structure markers are ASCII; binary streams
    // are skipped over by the regex anyway.
    const text = new TextDecoder('latin1').decode(buf);
    // Match `/Type /Page` followed by whitespace OR `>>` (end of dict),
    // but NOT `/Pages` (the tree root).
    const matches = text.match(/\/Type\s*\/Page(?![s])\b/g);
    return matches?.length ?? 0;
  } catch {
    return 0;
  }
}

// ── Styles ─────────────────────────────────────────────────────────────────

const panelSt: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  flexShrink: 0,
};

const selectSt: CSSProperties = {
  width: '100%',
  padding: '4px 6px',
  fontSize: 12,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
};

/** 각 장 오른쫽 위의 작은 장 도구(앞에 빈 장 넣기 · 이 장 지우기). */
const pageBtnSt: CSSProperties = {
  width: 22,
  height: 22,
  padding: 0,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  cursor: 'pointer',
  fontSize: 12,
  lineHeight: '20px',
};

const inputSt: CSSProperties = {
  padding: '4px 6px',
  fontSize: 12,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
};

// ── Sub-components ─────────────────────────────────────────────────────────

function PropRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 3 }}>{label}</div>
      {children}
    </div>
  );
}

// ── Left Panel ─────────────────────────────────────────────────────────────

interface LeftPanelProps {
  roles: SignerRole[];
  placingType: FieldType | null;
  onPlacingTypeChange: (t: FieldType | null) => void;
  onRolesChange: (roles: SignerRole[]) => void;
  // 개선계획 §5-4 (결정 ⑥-1A) — '첨부 자리' 토글.
  allowAttachment: boolean;
  onAllowAttachmentChange: (v: boolean) => void;
  // 2026-09-22 — 캔버스 객체 팔레트(표·사진·텍스트·도형). 필드 팔레트와 하나만 켜진다.
  placingObject: LayoutObjectKind | null;
  onPlacingObjectChange: (k: LayoutObjectKind | null) => void;
  onImportXlsx: () => void;
  /** 서식 관리(저장/불러오기) — 오너 요청으로 서명자 슬롯 아래에 둔다(2026-09-23). */
  onOpenPresets: () => void;
}

function LeftPanel({
  roles,
  placingType,
  onPlacingTypeChange,
  onRolesChange,
  allowAttachment,
  onAllowAttachmentChange,
  placingObject,
  onPlacingObjectChange,
  onImportXlsx,
  onOpenPresets,
}: LeftPanelProps) {
  const [addingRole, setAddingRole] = useState(false);
  const [newSlot, setNewSlot] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState(SIGNER_COLORS[0] ?? '#4A90D9');

  const handleAddRole = () => {
    if (!newSlot.trim() || !newLabel.trim()) return;
    onRolesChange([
      ...roles,
      { slot: newSlot.trim(), label: newLabel.trim(), required: true, color: newColor },
    ]);
    setAddingRole(false);
    setNewSlot('');
    setNewLabel('');
    setNewColor(SIGNER_COLORS[roles.length % SIGNER_COLORS.length] ?? '#4A90D9');
  };

  return (
    <div
      style={{
        ...panelSt,
        width: 200,
        borderRight: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
      }}
    >
      {/* ── 첨부 자리 (결정 ⑥-1A) ── */}
      <div style={{ padding: '12px 12px 8px' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 8,
          }}
        >
          첨부 문서
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={allowAttachment}
            onChange={(e) => onAllowAttachmentChange(e.target.checked)}
          />
          발송할 때 첨부 PDF 를 고른다
        </label>
        <p
          style={{
            margin: '6px 0 0',
            fontSize: 11,
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
          }}
        >
          첨부는 이 양식 뒤에 그대로 붙으며, 20MB, 30페이지 이하의 PDF만 됩니다. 첨부 페이지 위에는
          서명칸을 둘 수 없으므로 아래 편집 영역은 이 양식의 페이지까지만입니다.
        </p>
      </div>

      {/* ── Signer Roles section ── */}
      <div style={{ padding: '12px 12px 8px', borderTop: '1px solid var(--border-subtle)' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 8,
          }}
        >
          서명자 슬롯
        </div>

        {roles.map((role) => (
          <div
            key={role.slot}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 6px',
              borderRadius: 'var(--r-input)',
              marginBottom: 2,
              background: 'var(--bg-muted)',
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: role.color ?? 'var(--fg-subtle)',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 12,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {role.label}
            </span>
            <button
              onClick={() => onRolesChange(roles.filter((r) => r.slot !== role.slot))}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: 14,
                color: 'var(--fg-muted)',
                padding: '0 2px',
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <Icon name="x" size={14} aria-hidden />
            </button>
          </div>
        ))}

        {addingRole ? (
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <input
              autoFocus
              placeholder="슬롯 ID (예: customer)"
              value={newSlot}
              onChange={(e) => setNewSlot(e.target.value)}
              style={{ ...inputSt, fontSize: 11 }}
            />
            <input
              placeholder="표시 이름 (예: 고객)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              style={{ ...inputSt, fontSize: 11 }}
            />
            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {SIGNER_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setNewColor(c)}
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    background: c,
                    border:
                      newColor === c ? '2px solid var(--fg-default)' : '1px solid transparent',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={handleAddRole}
                style={{
                  flex: 1,
                  fontSize: 11,
                  padding: '3px 0',
                  borderRadius: 'var(--r-btn)',
                  border: '1px solid var(--border-default)',
                  cursor: 'pointer',
                  background: 'var(--bg-surface)',
                }}
              >
                추가
              </button>
              <button
                onClick={() => setAddingRole(false)}
                style={{
                  flex: 1,
                  fontSize: 11,
                  padding: '3px 0',
                  borderRadius: 'var(--r-btn)',
                  border: '1px solid var(--border-default)',
                  cursor: 'pointer',
                  background: 'var(--bg-surface)',
                }}
              >
                취소
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setAddingRole(true)}
            style={{
              width: '100%',
              marginTop: 4,
              fontSize: 11,
              padding: '4px 0',
              borderRadius: 'var(--r-btn)',
              border: '1px dashed var(--border-default)',
              cursor: 'pointer',
              background: 'transparent',
              color: 'var(--text-secondary)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
            }}
          >
            <Icon name="plus" size={12} aria-hidden /> 슬롯 추가
          </button>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--border-subtle)', margin: '4px 0' }} />

      {/* ── 객체 팔레트 (2026-09-22) — 빈 종이 위에 표·사진·텍스트·도형을 놓는다.
          한글/워드로 문서를 만들어 PDF 로 올리던 일을 콘솔 안에서 끝낸다. ── */}
      <div style={{ padding: '8px 12px' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 8,
          }}
        >
          팔레트
        </div>
        {/* 2026-09-23 — 두 팔레트가 글자 두께만 달라 '텍스트'를 잘못 누르던 것(오너 보고).
            이쪽은 **종이에 그대로 인쇄되는 것** — 회색 바탕·실선·아이콘. */}
        {/* 서식 관리 — 엑셀 가져오기 바로 위, 눈에 띄게(오너 요청 2026-09-23: 서명자 슬롯
            아래 흰 버튼이라 안 보였다). 엑셀 버튼(채움)과 갈리게 테두리형. */}
        <button
          type="button"
          data-testid="open-presets"
          onClick={onOpenPresets}
          title="그린 객체·필드를 서식으로 저장하거나, 저장된 서식을 불러옵니다"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            width: '100%',
            padding: '8px 8px',
            marginBottom: 6,
            borderRadius: 'var(--r-btn)',
            border: '1.5px solid var(--fg-brand)',
            background: 'color-mix(in srgb, var(--fg-brand) 8%, var(--bg-surface))',
            color: 'var(--fg-brand)',
            cursor: 'pointer',
            fontSize: 12.5,
            fontWeight: 700,
          }}
        >
          <Icon name="layers" size={16} aria-hidden />
          서식 관리
        </button>
        {/* 엑셀 가져오기 — 팔레트 맨 위, 눈에 띄게(오너 요청 2026-09-23) */}
        <button
          type="button"
          data-testid="import-xlsx"
          onClick={onImportXlsx}
          title="엑셀 표를 글자, 색, 테두리, 병합, 행 높이, 사진까지 그대로 가져옵니다. {{서명}}처럼 적은 칸은 입력 칸이 됩니다."
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            width: '100%',
            padding: '9px 8px',
            marginBottom: 8,
            borderRadius: 'var(--r-btn)',
            border: 'none',
            background: 'var(--fg-brand)',
            color: 'var(--bg-surface)',
            cursor: 'pointer',
            fontSize: 12.5,
            fontWeight: 700,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            aria-hidden
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
          >
            <rect x="1.5" y="2" width="13" height="12" rx="1.5" />
            <path d="M1.5 6h13M1.5 10h13M6 2v12" />
          </svg>
          엑셀에서 표 가져오기
        </button>
        {(['table', 'image', 'text', 'shape'] as LayoutObjectKind[]).map((kind) => (
          <button
            key={kind}
            data-testid={`palette-${kind}`}
            onClick={() => onPlacingObjectChange(placingObject === kind ? null : kind)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              width: '100%',
              textAlign: 'left',
              padding: '6px 8px',
              marginBottom: 3,
              borderRadius: 'var(--r-btn)',
              border: '1px solid',
              borderColor: placingObject === kind ? 'var(--blue-500)' : 'var(--border-default)',
              background: placingObject === kind ? 'var(--blue-50)' : 'var(--bg-muted)',
              color: placingObject === kind ? 'var(--blue-700)' : 'var(--fg-default)',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <ObjectPaletteIcon kind={kind} />
            {LAYOUT_OBJECT_LABELS[kind]}
          </button>
        ))}
      </div>

      <div style={{ borderTop: '1px solid var(--border-subtle)', margin: '4px 0' }} />

      {/* ── Field palette ── */}
      <div style={{ padding: '8px 12px' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 8,
          }}
        >
          필드 팔레트
        </div>
        {/* 이쪽은 **서명할 때 채우는 칸** — 청록 실선 테두리 + 흰 바탕(오너 결정 2026-09-23 — 점선 대신 실선). */}
        <div style={paletteCaptionSt}>서명할 때 채우는 칸</div>
        {FIELD_TYPE_OPTIONS.map(({ type, label }) => (
          <button
            key={type}
            data-testid={`field-palette-${type}`}
            onClick={() => onPlacingTypeChange(placingType === type ? null : type)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              width: '100%',
              textAlign: 'left',
              padding: '6px 8px',
              marginBottom: 3,
              borderRadius: 'var(--r-btn)',
              border: '1.5px solid',
              borderColor: placingType === type ? 'var(--blue-500)' : 'var(--teal-400)',
              background: placingType === type ? 'var(--blue-50)' : 'var(--bg-surface)',
              color: placingType === type ? 'var(--blue-700)' : 'var(--teal-800)',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 14,
                height: 10,
                flex: '0 0 auto',
                border: '1.5px solid currentColor',
                borderRadius: 2,
                opacity: 0.8,
              }}
            />
            {type === 'text' ? '텍스트 입력칸' : label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** 팔레트 제목 아래 한 줄 설명. */
const paletteCaptionSt: CSSProperties = {
  fontSize: 10.5,
  color: 'var(--text-secondary)',
  margin: '-4px 0 8px',
  lineHeight: 1.4,
};

/** 객체 팔레트 아이콘 — 인쇄되는 것(실선). 필드 팔레트의 점선 칸과 한눈에 갈린다. */
function ObjectPaletteIcon({ kind }: { kind: LayoutObjectKind }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.4,
    'aria-hidden': true,
    style: { flex: '0 0 auto', opacity: 0.75 },
  } as const;
  if (kind === 'table')
    return (
      <svg {...common}>
        <rect x="1.5" y="2.5" width="13" height="11" rx="1" />
        <path d="M1.5 6.5h13M1.5 10h13M6 2.5v11" />
      </svg>
    );
  if (kind === 'image')
    return (
      <svg {...common}>
        <rect x="1.5" y="2.5" width="13" height="11" rx="1" />
        <circle cx="5.5" cy="6" r="1.3" />
        <path d="M2 12.5l4-4 3 3 2-2 3.5 3" />
      </svg>
    );
  if (kind === 'text')
    return (
      <svg {...common}>
        <path d="M3 3.5h10M8 3.5v9.5M6 13h4" />
      </svg>
    );
  return (
    <svg {...common}>
      <rect x="2" y="2" width="7.5" height="7.5" rx="0.5" />
      <circle cx="10.5" cy="10.5" r="3.5" />
    </svg>
  );
}

// ── Right Panel ────────────────────────────────────────────────────────────

interface RightPanelProps {
  field: SignatureField | null;
  roles: SignerRole[];
  onFieldChange: (f: SignatureField) => void;
  onFieldDelete: (id: string) => void;
  /** 패널 너비(px) — 사용자가 왼쪽 경계를 끌어 바꾼다. */
  width: number;
}

function RightPanel({ field, roles, onFieldChange, onFieldDelete, width }: RightPanelProps) {
  if (!field) {
    return (
      <div
        style={{
          ...panelSt,
          width,
          borderLeft: '1px solid var(--border-subtle)',
          background: 'var(--bg-surface)',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <p
          style={{
            color: 'var(--text-secondary)',
            fontSize: 13,
            textAlign: 'center',
            lineHeight: 1.7,
          }}
        >
          필드를 클릭하거나
          <br />
          팔레트에서 유형을 선택한 후
          <br />
          PDF 위를 클릭해 배치하세요.
        </p>
      </div>
    );
  }

  const update = (patch: Partial<SignatureField>) => onFieldChange({ ...field, ...patch });

  const bindingKind = field.binding.kind;
  const bindingPath =
    field.binding.kind !== 'literal' ? (field.binding as { kind: string; path: string }).path : '';

  return (
    <div
      style={{
        ...panelSt,
        width,
        borderLeft: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
      }}
    >
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14 }}>필드 속성</div>

        {/* Type */}
        <PropRow label="유형">
          <select
            value={field.type}
            onChange={(e) => update({ type: e.target.value as FieldType })}
            style={selectSt}
          >
            {FIELD_TYPE_OPTIONS.map(({ type, label }) => (
              <option key={type} value={type}>
                {label}
              </option>
            ))}
          </select>
        </PropRow>

        {/* Signer slot */}
        <PropRow label="서명자 슬롯">
          <select
            value={field.signerSlot ?? ''}
            onChange={(e) => update({ signerSlot: e.target.value || null })}
            style={selectSt}
          >
            <option value="">미지정</option>
            {roles.map((r) => (
              <option key={r.slot} value={r.slot}>
                {r.label}
              </option>
            ))}
          </select>
        </PropRow>
        {/* 서명 무반응 버그(2026-06-12) 후속 — 미지정 슬롯의 실제 동작 안내.
            서버가 미지정/고아 슬롯의 수동 입력 필드를 첫 서명자에게 귀속
            (자동 채움 binding 은 서명자 화면 비표시 + 합성)하므로, 디자이너가
            의도를 알 수 있게 명시한다. */}
        {!field.signerSlot && (
          <div
            className="kuun-caption"
            style={{ color: 'var(--fg-muted)', padding: '0 0 6px', lineHeight: 1.5 }}
            data-testid="field-slot-unassigned-hint"
          >
            {field.binding && field.binding.kind !== 'literal'
              ? '자동 채움 값으로 채워지며, 서명자 화면에는 보이지 않습니다.'
              : '슬롯을 정하지 않으면 첫 번째 서명자 화면에 입력 칸으로 보입니다.'}
          </div>
        )}

        {/* Data binding kind */}
        <PropRow label="데이터 바인딩">
          <select
            value={bindingKind}
            onChange={(e) => {
              const kind = e.target.value as DataBinding['kind'];
              if (kind === 'literal') {
                update({ binding: { kind: 'literal' } });
              } else if (kind === 'robot') {
                update({ binding: { kind: 'robot', path: 'serialNumber' } });
              } else if (kind === 'customer') {
                update({ binding: { kind: 'customer', path: 'businessName' } });
              } else {
                update({
                  binding: {
                    kind: 'signer',
                    slot: field.signerSlot ?? '',
                    path: 'name',
                  },
                });
              }
            }}
            style={selectSt}
          >
            <option value="literal">수동 입력</option>
            <option value="robot">로봇 정보</option>
            <option value="customer">고객사 정보</option>
            <option value="signer">서명자 정보</option>
          </select>
        </PropRow>

        {/* Binding sub-path */}
        {bindingKind === 'robot' && (
          <PropRow label="로봇 필드">
            <select
              value={bindingPath}
              onChange={(e) =>
                update({
                  binding: {
                    kind: 'robot',
                    path: e.target.value as 'serialNumber' | 'modelName',
                  },
                })
              }
              style={selectSt}
            >
              <option value="serialNumber">시리얼 번호</option>
              <option value="modelName">모델명</option>
            </select>
          </PropRow>
        )}
        {bindingKind === 'customer' && (
          <PropRow label="고객사 필드">
            <select
              value={bindingPath}
              onChange={(e) =>
                update({
                  binding: {
                    kind: 'customer',
                    path: e.target.value as
                      | 'businessName'
                      | 'businessNumber'
                      | 'ceoName'
                      | 'address',
                  },
                })
              }
              style={selectSt}
            >
              <option value="businessName">상호명</option>
              <option value="businessNumber">사업자번호</option>
              <option value="ceoName">대표자명</option>
              <option value="address">주소</option>
            </select>
          </PropRow>
        )}
        {bindingKind === 'signer' && (
          <PropRow label="서명자 필드">
            <select
              value={bindingPath}
              onChange={(e) =>
                update({
                  binding: {
                    kind: 'signer',
                    slot: field.signerSlot ?? '',
                    path: e.target.value as 'name' | 'phone',
                  },
                })
              }
              style={selectSt}
            >
              <option value="name">이름</option>
              <option value="phone">전화번호</option>
            </select>
          </PropRow>
        )}

        {/* Required */}
        <PropRow label="필수 여부">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => update({ required: e.target.checked })}
            />
            <span style={{ fontSize: 12 }}>필수</span>
          </label>
        </PropRow>

        {/* Placeholder (text / dropdown) */}
        {(field.type === 'text' || field.type === 'dropdown') && (
          <PropRow label="플레이스홀더">
            <input
              value={field.placeholder ?? ''}
              onChange={(e) => update({ placeholder: e.target.value || null })}
              style={{ ...inputSt, width: '100%', boxSizing: 'border-box' }}
            />
          </PropRow>
        )}

        {/* Position / size */}
        <div
          style={{
            borderTop: '1px solid var(--border-subtle)',
            paddingTop: 10,
            marginTop: 6,
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
            위치 / 크기 (px)
          </div>
          {/* round5 #6b/#6d — NumberField: blur/Enter 커밋(즉시 clamp 의
              '55→105' 버그 해소) + Y 는 화살표 방향을 시각 방향과 정합
              (▼=아래로 이동=y+1). 저장 좌표계(top-left)는 무변경. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <NumberField label="X" value={field.x} min={0} onCommit={(v) => update({ x: v })} />
            <NumberField
              label="Y (위에서부터)"
              value={field.y}
              min={0}
              invertArrows
              onCommit={(v) => update({ y: v })}
            />
            <NumberField
              label="너비"
              value={field.w}
              min={minW(field.type)}
              onCommit={(v) => {
                update({ w: v });
                // round5 #6a — 직접 입력한 크기도 다음 배치 기본값으로 기억.
                lastSizeByType[field.type] = { w: v, h: field.h };
              }}
            />
            <NumberField
              label="높이"
              value={field.h}
              min={minH(field.type)}
              onCommit={(v) => {
                update({ h: v });
                lastSizeByType[field.type] = { w: field.w, h: v };
              }}
            />
          </div>
        </div>

        {/* Alignment (single-field horizontal align within page) */}
        <div
          style={{
            borderTop: '1px solid var(--border-subtle)',
            paddingTop: 10,
            marginTop: 10,
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>정렬</div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={() => update({ x: 0 })}
              style={{
                flex: 1,
                fontSize: 11,
                padding: '4px 0',
                borderRadius: 'var(--r-btn)',
                border: '1px solid var(--border-default)',
                background: 'var(--bg-surface)',
                cursor: 'pointer',
              }}
              title="왼쪽 정렬 (x = 0)"
            >
              왼쪽
            </button>
            <button
              onClick={() => update({ x: Math.round((PAGE_W - field.w) / 2) })}
              style={{
                flex: 1,
                fontSize: 11,
                padding: '4px 0',
                borderRadius: 'var(--r-btn)',
                border: '1px solid var(--border-default)',
                background: 'var(--bg-surface)',
                cursor: 'pointer',
              }}
              title="가운데 정렬"
            >
              가운데
            </button>
            <button
              onClick={() => update({ x: PAGE_W - field.w })}
              style={{
                flex: 1,
                fontSize: 11,
                padding: '4px 0',
                borderRadius: 'var(--r-btn)',
                border: '1px solid var(--border-default)',
                background: 'var(--bg-surface)',
                cursor: 'pointer',
              }}
              title="오른쪽 정렬"
            >
              오른쪽
            </button>
          </div>
        </div>

        {/* 텍스트 정렬 (수평) — text/date/dropdown 에만 노출.
            acceptance-ux-changes-2026-06 §A4 / D3. 기본값은 'left'(역호환). */}
        {(field.type === 'text' || field.type === 'date' || field.type === 'dropdown') && (
          <div
            style={{
              borderTop: '1px solid var(--border-subtle)',
              paddingTop: 10,
              marginTop: 10,
            }}
          >
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
              텍스트 정렬
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {(
                [
                  ['left', '좌'],
                  ['center', '중앙'],
                  ['right', '우'],
                ] as const
              ).map(([a, label]) => {
                const active = (field.align ?? 'left') === a;
                return (
                  <button
                    key={a}
                    onClick={() => update({ align: a })}
                    style={{
                      flex: 1,
                      fontSize: 11,
                      padding: '4px 0',
                      borderRadius: 'var(--r-btn)',
                      border: active ? '1px solid var(--brand)' : '1px solid var(--border-default)',
                      background: active ? 'var(--bg-info-subtle)' : 'var(--bg-surface)',
                      color: active ? 'var(--brand)' : 'inherit',
                      fontWeight: active ? 700 : 400,
                      cursor: 'pointer',
                    }}
                    title={`텍스트 ${label} 정렬`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Delete */}
        <div style={{ marginTop: 16 }}>
          <Button
            variant="danger"
            onClick={() => onFieldDelete(field.id)}
            style={{ width: '100%' }}
          >
            필드 삭제
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

/** 사진 파일의 원본 픽셀 크기 — 놓을 때 비율을 지키려고. 못 읽으면 null(기본 크기로 놓는다). */
function readImageSize(file: File): Promise<{ w: number; h: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ w: img.naturalWidth, h: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

/** 오른쪽 속성 패널 너비(px). 사용자가 끌어 바꾸고, 브라우저에 기억한다. */
const RIGHT_PANEL_DEFAULT_W = 300;
const RIGHT_PANEL_MIN_W = 240;
const RIGHT_PANEL_MAX_W = 640;
const RIGHT_PANEL_W_KEY = 'bhops.signDesigner.rightPanelWidth';

function loadRightPanelW(): number {
  try {
    const v = Number(window.localStorage.getItem(RIGHT_PANEL_W_KEY));
    if (Number.isFinite(v) && v >= RIGHT_PANEL_MIN_W && v <= RIGHT_PANEL_MAX_W) return v;
  } catch {
    // 저장소를 못 쓰는 창(사생활 모드 등)은 기본값.
  }
  return RIGHT_PANEL_DEFAULT_W;
}

/** Undo/Redo 스냅숏 — 필드와 캔버스 객체를 함께 되돌린다(2026-09-22). */
interface HistorySnapshot {
  fields: SignatureField[];
  objects: LayoutObject[];
}

interface DragState {
  id: string;
  startMouseX: number;
  startMouseY: number;
  startFieldX: number;
  startFieldY: number;
  /**
   * True once the cursor has moved at least 1px in document space.
   * Prevents phantom history entries when a snap-to-grid click on a
   * non-aligned field mouseup-snaps the field but the user never dragged.
   */
  actuallyMoved: boolean;
}

type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

interface ResizeState {
  id: string;
  handle: ResizeHandle;
  startMouseX: number;
  startMouseY: number;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
}

/**
 * Drag-to-place — 팔레트에서 타입을 고른 뒤 페이지 위에서 드래그해 크기를
 * 직접 그리는 상태. 좌표는 전부 문서 공간(zoom 나눗셈 후)이다.
 *
 * 클릭(= 기억된 크기로 생성)과 드래그(= 그린 크기로 생성)는 같은
 * mousedown 에서 출발하므로, mouseup 시점에 이동 거리로 갈라진다.
 */
interface PlaceDragState {
  type: FieldType;
  startX: number;
  startY: number;
  curX: number;
  curY: number;
  /** 임계값을 넘겨 '드래그'로 확정됐는지. false 면 클릭으로 처리. */
  moved: boolean;
}

/**
 * 클릭 / 드래그를 가르는 이동 거리(문서 공간 px). 손떨림으로 1~2px 움직여도
 * 클릭으로 인식되도록 하는 완충값.
 */
const PLACE_DRAG_THRESHOLD = 5;

/** Round value to nearest multiple of step (used by snap-to-grid). */
function snap(value: number, step: number): number {
  return Math.round(value / step) * step;
}

const GRID_SIZE = 8;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.0;
const ZOOM_STEP = 0.25;
// Ctrl+휠은 한 칸에 10% — 버튼보다 잘게.
const WHEEL_ZOOM_STEP = 0.1;

/**
 * 짧은 확인 창 — **Enter 가 확인**, Esc 가 취소(오너 요청 2026-09-23). 확인 버튼에
 * 포커스를 두고, 창이 떠 있는 동안 다른 단축키(디자이너의 Delete 등)가 먹지 않게
 * 키를 여기서 가로챈다.
 */
function ConfirmDialog({
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const okRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    okRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onConfirm();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.stopPropagation();
      }
    }
    // capture 로 먼저 받아 디자이너 단축키보다 앞선다.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onConfirm, onCancel]);
  return (
    <div
      role="alertdialog"
      aria-label={message}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        background: 'var(--bg-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          width: 340,
          background: 'var(--bg-surface)',
          borderRadius: 'var(--r-card)',
          boxShadow: 'var(--shadow-lg)',
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>{message}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onCancel}>
            취소
          </Button>
          <button
            ref={okRef}
            type="button"
            data-testid="confirm-ok"
            onClick={onConfirm}
            style={{
              padding: '7px 16px',
              border: 'none',
              borderRadius: 'var(--r-btn)',
              background: 'var(--fg-danger)',
              color: 'var(--bg-surface)',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {confirmLabel} (Enter)
          </button>
        </div>
      </div>
    </div>
  );
}

export function SignTemplateDesignerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const centerPanelRef = useRef<HTMLDivElement>(null);
  // True while a programmatic smooth-scroll is in flight (page-jump dropdown).
  // The scroll handler skips currentPage updates during this window so the
  // dropdown doesn't bounce through intermediate pages mid-animation.
  const isProgrammaticScrollRef = useRef(false);
  const programmaticScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── State ──────────────────────────────────────────────────────────────
  const [fields, setFields] = useState<SignatureField[]>([]);
  // §A6 — 겹친 필드 id 집합(경고 표시용).
  const overlappingIds = useMemo(() => findOverlappingFieldIds(fields), [fields]);
  const [roles, setRoles] = useState<SignerRole[]>([]);
  const [templateName, setTemplateName] = useState('');
  // 개선계획 §5-4 (결정 ⑥-1A) — 발송 시 첨부 PDF 를 고르는 양식인지.
  const [allowAttachment, setAllowAttachment] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [placingType, setPlacingType] = useState<FieldType | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [uploading, setUploading] = useState(false);

  // ── 캔버스 객체 (2026-09-22) ─────────────────────────────────────────────
  // 표·텍스트·도형·사진을 종이 위에 직접 놓는다. 객체는 **저작 수단**이고 서버가
  // 거기서 PDF 를 만들어 기존 `pdfS3Key` 자리에 넣으므로, 서명·합성·포털은 바뀌지
  // 않는다. 편집 상태는 useLayoutEditor(아래)에 있다.
  /** 사진 객체의 s3Key → 화면에 보일 URL(서버 presigned 또는 방금 올린 파일의 blob). */
  const [assetUrls, setAssetUrls] = useState<Record<string, string>>({});
  /**
   * 장 구성 — i 번째 장에 밑그림의 몇 번째 장을 깔지(`null` = 빈 장). `null` 이면 아직
   * 장을 넣고 뺀 적이 없어 밑그림 장을 차례로 깐다(서버 규약과 같다).
   */
  const [pages, setPages] = useState<(number | null)[] | null>(null);
  /** 서식(프리셋) 대화 상자. */
  const [presetOpen, setPresetOpen] = useState(false);

  // ── 오른쪽 패널 너비 — 왼쪽 경계를 끌어 바꾼다(오너 요청 2026-09-23) ─────────
  const [rightW, setRightW] = useState<number>(loadRightPanelW);
  const [rightResizing, setRightResizing] = useState(false);
  const saveRightW = useCallback((w: number) => {
    const v = Math.round(Math.max(RIGHT_PANEL_MIN_W, Math.min(RIGHT_PANEL_MAX_W, w)));
    setRightW(v);
    try {
      window.localStorage.setItem(RIGHT_PANEL_W_KEY, String(v));
    } catch {
      // 기억 못 해도 지금 화면에는 먹는다.
    }
  }, []);
  const beginRightResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = rightW;
      setRightResizing(true);
      const onMove = (ev: MouseEvent) => {
        // 경계를 왼쪽으로 끌면 넓어진다.
        const w = Math.max(
          RIGHT_PANEL_MIN_W,
          Math.min(RIGHT_PANEL_MAX_W, startW - (ev.clientX - startX)),
        );
        setRightW(Math.round(w));
      };
      const onUp = (ev: MouseEvent) => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        setRightResizing(false);
        saveRightW(startW - (ev.clientX - startX));
      };
      document.body.style.cursor = 'col-resize';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [rightW, saveRightW],
  );
  /** '이 표를 삭제하시겠습니까?' 확인 중인 표 id (2026-09-23 오너 요청). */
  const [confirmDeleteTableId, setConfirmDeleteTableId] = useState<string | null>(null);
  /** 여럿 지우기 확인 — 표가 섞여 있을 때만 묻는다. */
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState<string[] | null>(null);
  /**
   * 선택 상자 — 빈 곳을 누르고 끌면 그려지고, 닿은 객체들을 함께 고른다(2026-09-23 오너
   * 요청). 끌지 않고 떼면 '빈 곳 한 번 누르기' — 고른 것을 모두 푼다.
   */
  const [marquee, setMarquee] = useState<{
    sx: number;
    sy: number;
    cx: number;
    cy: number;
    moved: boolean;
    add: boolean;
  } | null>(null);
  /** 표 칸 사진 등, 라이브러리를 모달로 띄워 고르는 중이면 그 약속. */
  const [cellImagePick, setCellImagePick] = useState<{
    resolve: (v: PickedImage | null) => void;
  } | null>(null);
  /** 팔레트 '사진' 에서 고른 뒤 종이를 누르길 기다리는 사진. */
  const pendingAssetRef = useRef<AssetDragPayload | null>(null);
  const [pendingAssetKey, setPendingAssetKey] = useState<string | null>(null);
  /** 화면에 깔린 PDF(밑그림)의 장 수. 객체 양식이면 렌더 결과가 아니라 밑그림이다. */
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfPageCount, setPdfPageCount] = useState(1);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [resize, setResize] = useState<ResizeState | null>(null);
  const [placeDrag, setPlaceDrag] = useState<PlaceDragState | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // 배치 직후의 click 이벤트를 한 번 삼킨다. 배치는 document mouseup 에서
  // 끝나는데, 뒤이어 버블링되는 click 을 중앙 패널의 "빈 곳 클릭 = 선택 해제"
  // 핸들러가 받으면 방금 만든 필드가 곧바로 선택 해제된다.
  const suppressNextOverlayClickRef = useRef(false);

  // ── Ctrl+C / Ctrl+V 클립보드 ───────────────────────────────────────────
  // 앱 내부 클립보드다. OS 클립보드(navigator.clipboard)를 쓰지 않는 이유:
  //   - 비동기 권한 프롬프트가 필요하고 Tauri/브라우저 동작이 갈린다
  //   - 디자이너 밖에서 복사한 텍스트가 필드로 붙는 혼란을 막는다
  // 입력 요소에 포커스가 있을 땐 아래 keydown 핸들러가 통째로 bail 하므로
  // 텍스트 복사/붙여넣기는 평소대로 동작한다.
  const clipboardRef = useRef<SignatureField | null>(null);
  // 연속 붙여넣기 시 계단식으로 어긋나게 하는 카운터. 복사할 때 0 으로 리셋.
  const pasteSeqRef = useRef(0);

  // ── Undo/Redo history ──────────────────────────────────────────────────
  // Snapshot stack of field arrays. Capped at 50 to avoid memory bloat
  // (typical templates have <20 fields, but better safe than OOM).
  //
  // Stored in a ref to avoid stale-closure bugs: rapid back-to-back edits
  // in the same tick (drag mouseup + keyboard nudge fired sequentially)
  // previously truncated based on an outdated historyIdx captured by
  // useCallback, dropping the snapshot that was just pushed.
  // `historyTick` is a render-trigger so canUndo/canRedo update in the UI.
  // 2026-09-22 — 스냅숏에 캔버스 객체도 담는다. 표를 고치고 Ctrl+Z 를 누르면 되돌아가야 한다.
  const historyRef = useRef<{ stack: HistorySnapshot[]; idx: number }>({ stack: [], idx: -1 });
  const [, setHistoryTick] = useState(0);

  // ── Snap-to-grid toggle ────────────────────────────────────────────────
  const [snapToGrid, setSnapToGrid] = useState(false);

  // ── Zoom ───────────────────────────────────────────────────────────────
  const [zoom, setZoom] = useState(1);
  const queryClient = useQueryClient();

  // ── 캔버스 객체 편집기 ───────────────────────────────────────────────────
  /**
   * 사진 고르기 — 팔레트 '사진' 에서 이미 고른 사진이 있으면 그것(놓기 모드), 없으면
   * 라이브러리를 모달로 띄워 고른다(표 칸 사진 등). 파일 대화 상자는 라이브러리 안에
   * 있다 — 불러온 사진은 거기 자동으로 쌓인다(오너 요청).
   */
  const pickImage = useCallback(async (): Promise<PickedImage | null> => {
    const pending = pendingAssetRef.current;
    if (pending) {
      pendingAssetRef.current = null;
      setPendingAssetKey(null);
      setAssetUrls((m) => ({ ...m, [pending.s3Key]: pending.url }));
      return {
        s3Key: pending.s3Key,
        fileName: pending.fileName,
        naturalW: pending.naturalW,
        naturalH: pending.naturalH,
      };
    }
    return new Promise<PickedImage | null>((resolve) => setCellImagePick({ resolve }));
  }, []);

  // 객체 바뀜 → 이력·dirty. pushHistory 는 아래에서 만들어지므로 ref 로 늦게 묶는다.
  const layoutChangeRef = useRef<(objects: LayoutObject[], meta: LayoutChangeMeta) => void>(
    () => {},
  );

  const layout = useLayoutEditor({
    zoom,
    docH: Math.max(pdfPageCount, pages?.length ?? 0) * PAGE_H,
    pickImage,
    onChange: (objects, meta) => layoutChangeRef.current(objects, meta),
  });
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  // ── 사진 주소 — 모르는 키는 받아 오고, 안 열리는 키(만료)는 다시 받는다 ──────
  // presigned 주소는 15분짜리라 오래 켜 둔 디자이너에서는 사진이 깨진다. 서식을 불러와 붙인
  // 사진은 처음부터 주소가 없다(2026-09-23 오너: "사진이 안 불러와지는데").
  const assetUrlsRef = useRef(assetUrls);
  assetUrlsRef.current = assetUrls;
  const refreshingRef = useRef(new Set<string>());
  const refreshAssetUrls = useCallback(async (keys: string[]) => {
    const want = keys.filter((k) => !refreshingRef.current.has(k));
    if (want.length === 0) return;
    want.forEach((k) => refreshingRef.current.add(k));
    try {
      const fresh = await signatureLayoutAssetsApi.freshUrls(want);
      if (Object.keys(fresh).length > 0) setAssetUrls((m) => ({ ...m, ...fresh }));
    } catch {
      // 다음 실패 때 다시 묻는다.
    } finally {
      // 같은 키가 또 깨지면(다시 만료) 또 받을 수 있게, 잠깐 뒤에 푼다.
      setTimeout(() => want.forEach((k) => refreshingRef.current.delete(k)), 5000);
    }
  }, []);
  const imageKeysSig = layoutImageKeys(layout.objects).join('|');
  useEffect(() => {
    const missing = layoutImageKeys(layoutRef.current.objects).filter(
      (k) => !assetUrlsRef.current[k],
    );
    if (missing.length > 0) void refreshAssetUrls(missing);
  }, [imageKeysSig, refreshAssetUrls]);

  /** 문서 장 수 — 장 구성(없으면 밑그림 장 수)과 객체가 차지한 장 중 큰 쪽. */
  const pageCount = Math.max(pages?.length ?? pdfPageCount, layoutPageCount(layout.objects));

  /**
   * 표 칸이 만든 입력 칸. 상태에 두지 않고 객체에서 매번 계산한다 — 서버도 저장할
   * 때 **같은 함수**로 만들므로 두 벌이 어긋날 자리가 없다.
   */
  const autoFields = useMemo(() => layoutAutoFields(layout.objects), [layout.objects]);

  const xlsxInputRef = useRef<HTMLInputElement>(null);
  const handleXlsxChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      try {
        const got = await signatureTemplatesApi.xlsxToLayout(file, roles[0]?.slot ?? 'customer');
        if (got.objects.length === 0) {
          toast.error(got.notes.join(' ') || '가져올 표를 찾지 못했습니다.');
          return;
        }
        setAssetUrls((m) => ({ ...m, ...got.assetUrls }));
        layout.appendObjects(got.objects);
        toast.success(
          `표 ${got.objects.filter((o) => o.kind === 'table').length}개를 가져왔습니다.` +
            (got.notes.length ? ` ${got.notes.join(' ')}` : ''),
        );
      } catch (err) {
        toast.error(extractApiError(err), '엑셀 가져오기 실패');
      }
    },
    [roles, layout, toast],
  );

  // 저장이 스스로 일으킨 이동은 막지 않는다.
  //
  // 새 버전 fork 시 onSuccess 가 setIsDirty(false) 직후 navigate() 를 호출하는데,
  // setState 는 비동기라 blocker 판정 시점엔 isDirty 가 아직 true 다. 그 결과
  // **저장 성공 직후 '저장하지 않은 변경사항' 이 뜨고 이동이 차단**됐다.
  //
  // 차단된 운영자는 보관(archived) 처리된 옛 버전 화면에 남고, 그 상태에서 다시
  // 저장하면 또 fork → 또 차단 → 버전만 계속 쌓인다. 저장 경로에서 이 ref 를
  // 동기적으로 세워 판정을 건너뛴다.
  const skipBlockerOnceRef = useRef(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (skipBlockerOnceRef.current) {
      skipBlockerOnceRef.current = false;
      return false;
    }
    return isDirty && currentLocation.pathname !== nextLocation.pathname;
  });

  // Page-count confirm modal state (replaces window.prompt — F1 follow-up).
  const [pendingUpload, setPendingUpload] = useState<{
    file: File;
    detectedPageCount: number;
  } | null>(null);
  const [pendingPageCountInput, setPendingPageCountInput] = useState('1');

  // ── Load template ─────────────────────────────────────────────────────
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['signature-template-detail', id],
    queryFn: () => signatureTemplatesApi.get(id!),
    enabled: !!id,
  });

  // Track isDirty in a ref so the seed effect can guard against clobbering
  // local edits when React Query refetches (window refocus, reconnect) —
  // without forcing isDirty into the effect deps (which would re-fire the
  // seed every time the operator's first edit flips isDirty true→false).
  const isDirtyRef = useRef(false);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    if (!data) return;
    // F24 — If the operator has unsaved local changes, do NOT overwrite
    // them with a refetched server snapshot. Their working state is
    // canonical until they Save or explicitly discard.
    if (isDirtyRef.current) return;
    // 객체가 만든 필드(lay_)는 객체에서 다시 계산한다 — 상태에 두면 두 벌이 된다.
    const initialFields = (data.fieldsJson ?? []).filter((f) => !isLayoutGeneratedField(f.id));
    setFields(initialFields);
    setRoles(data.signerRolesJson ?? []);
    setTemplateName(data.name);
    setAllowAttachment(data.allowAttachment ?? false);
    // 객체 밑에 깔 PDF(밑그림). 렌더 결과를 깔면 객체가 두 겹으로 보인다.
    setPdfUrl(data.basePdfDownloadUrl || null);
    setPdfPageCount(data.pageCount || 1);
    const rawLayout = data.layoutJson as TemplateLayout | null;
    const validLayout =
      rawLayout && validateTemplateLayout(rawLayout).length === 0 ? rawLayout : null;
    const seededObjects = validLayout ? validLayout.objects : [];
    layout.setObjectsSilently(seededObjects);
    setPages(validLayout?.pages ?? null);
    setAssetUrls(data.assetUrls ?? {});
    setIsDirty(false);
    // Seed history with the loaded snapshot so the first edit becomes
    // undoable (canUndo = idx > 0 → first push lands at idx=1).
    historyRef.current = { stack: [{ fields: initialFields, objects: seededObjects }], idx: 0 };
    setHistoryTick(0);
  }, [data, layout.setObjectsSilently]);

  // ── Undo/Redo helpers ──────────────────────────────────────────────────
  // Push a snapshot to history. Call AFTER setFields(next) for user actions.
  // Reads/writes the ref directly so back-to-back pushes in the same tick
  // never see a stale idx.
  const pushHistory = useCallback((next: SignatureField[], nextObjects?: LayoutObject[]) => {
    const current = historyRef.current;
    const truncated = current.stack.slice(0, current.idx + 1);
    truncated.push({ fields: next, objects: nextObjects ?? layoutRef.current.objects });
    const capped = truncated.length > 50 ? truncated.slice(truncated.length - 50) : truncated;
    historyRef.current = { stack: capped, idx: capped.length - 1 };
    setHistoryTick((t) => t + 1);
  }, []);

  // F1 — Replace the current head of the history stack instead of pushing
  // a new snapshot. Used to coalesce rapid sequential edits to the SAME
  // field's properties (e.g. typing "180" into a number input emits 3
  // onChange events → 3 keystrokes collapse into 1 undoable action).
  // Falls back to pushHistory if the stack is empty.
  const replaceLastHistory = useCallback((next: SignatureField[], nextObjects?: LayoutObject[]) => {
    const current = historyRef.current;
    const snap = { fields: next, objects: nextObjects ?? layoutRef.current.objects };
    if (current.idx < 0) {
      historyRef.current = { stack: [snap], idx: 0 };
      setHistoryTick((t) => t + 1);
      return;
    }
    current.stack[current.idx] = snap;
    setHistoryTick((t) => t + 1);
  }, []);

  // F1 — Signature of the last property-edit pushed via RightPanel.
  // We coalesce subsequent edits within EDIT_COALESCE_MS that target the
  // SAME field id into a single history entry.
  //
  // `historyIdx` is captured at push-time: if any OTHER pushHistory call
  // (drag/resize/keyboard nudge/delete) advances the stack in between,
  // historyRef.current.idx will no longer match and we fall back to
  // pushing a new snapshot — guaranteeing we never overwrite an unrelated
  // history entry.
  const lastEditSignatureRef = useRef<{
    id: string;
    kind: 'nudge' | 'panel-edit' | 'layout';
    timestamp: number;
    historyIdx: number;
  } | null>(null);

  /** 이력에서 객체를 되살린다 — onChange 를 타지 않게 조용히, 없어진 객체의 선택은 푼다. */
  function restoreObjects(objects: LayoutObject[]) {
    const lay = layoutRef.current;
    lay.setObjectsSilently(objects);
    if (lay.selectedObjId && !objects.some((o) => o.id === lay.selectedObjId))
      lay.selectObject(null);
  }

  const undo = useCallback(() => {
    const current = historyRef.current;
    if (current.idx <= 0) return;
    const newIdx = current.idx - 1;
    historyRef.current = { stack: current.stack, idx: newIdx };
    const snapshot = current.stack[newIdx]!;
    setFields(snapshot.fields);
    restoreObjects(snapshot.objects);
    // Clear selection only if the previously-selected field no longer exists
    // in the restored snapshot (otherwise the user keeps their editing context).
    setSelectedId((sid) => (sid && !snapshot.fields.find((f) => f.id === sid) ? null : sid));
    setHistoryTick((t) => t + 1);
    setIsDirty(true);
  }, []);

  const redo = useCallback(() => {
    const current = historyRef.current;
    if (current.idx >= current.stack.length - 1) return;
    const newIdx = current.idx + 1;
    historyRef.current = { stack: current.stack, idx: newIdx };
    const snapshot = current.stack[newIdx]!;
    setFields(snapshot.fields);
    restoreObjects(snapshot.objects);
    setSelectedId((sid) => (sid && !snapshot.fields.find((f) => f.id === sid) ? null : sid));
    setHistoryTick((t) => t + 1);
    setIsDirty(true);
  }, []);

  const canUndo = historyRef.current.idx > 0;
  const canRedo = historyRef.current.idx < historyRef.current.stack.length - 1;

  // 객체 바뀜 → dirty + 이력. 글자 치기(coalesce)는 EDIT_COALESCE_MS 안이면 앞 항목에 묶는다
  // — 필드 속성 편집과 같은 규칙. 매 렌더 다시 묶어 최신 fields 를 본다.
  layoutChangeRef.current = (objects, meta) => {
    setIsDirty(true);
    const now = Date.now();
    const sig = lastEditSignatureRef.current;
    const coalesce =
      meta.coalesce &&
      sig !== null &&
      sig.kind === 'layout' &&
      now - sig.timestamp < EDIT_COALESCE_MS &&
      sig.historyIdx === historyRef.current.idx;
    if (coalesce) replaceLastHistory(fields, objects);
    else pushHistory(fields, objects);
    lastEditSignatureRef.current = {
      id: 'layout',
      kind: 'layout',
      timestamp: now,
      historyIdx: historyRef.current.idx,
    };
  };

  // ── 장 넣기·지우기 (2026-09-22) ──────────────────────────────────────────
  // 객체는 공유 함수가 옮기고, 필드는 같은 규칙으로 여기서 옮긴다(한 장 = PAGE_H).
  const effectivePages = () => pages ?? layoutDefaultPages(pdfPageCount, layoutRef.current.objects);
  const insertBlankPage = (at: number) => {
    const next = layoutInsertBlankPage(
      { objects: layoutRef.current.objects, basePdfS3Key: null, pages: effectivePages() },
      at,
      pdfPageCount,
    );
    const nextFields = fields.map((f) =>
      f.pageIndex >= at ? { ...f, y: f.y + PAGE_H, pageIndex: f.pageIndex + 1 } : f,
    );
    layoutRef.current.setObjectsSilently(next.objects);
    setPages(next.pages ?? null);
    setFields(nextFields);
    pushHistory(nextFields, next.objects);
    lastEditSignatureRef.current = null;
    setIsDirty(true);
  };
  const deletePage = (i: number) => {
    const cur = effectivePages();
    if (cur.length <= 1) return;
    const next = layoutDeletePage(
      { objects: layoutRef.current.objects, basePdfS3Key: null, pages: cur },
      i,
      pdfPageCount,
    );
    const nextFields = fields
      .filter((f) => f.pageIndex !== i)
      .map((f) => (f.pageIndex > i ? { ...f, y: f.y - PAGE_H, pageIndex: f.pageIndex - 1 } : f));
    layoutRef.current.setObjectsSilently(next.objects);
    layoutRef.current.selectObject(null);
    setSelectedId(null);
    setPages(next.pages ?? null);
    setFields(nextFields);
    pushHistory(nextFields, next.objects);
    lastEditSignatureRef.current = null;
    setIsDirty(true);
  };

  /**
   * 서식 불러오기 — 객체와 필드를 **한 번에** 붙인다(Ctrl+Z 한 번에 되돌아가게).
   * · id 는 새로 준다(같은 서식을 두 번 불러도 겹치지 않게).
   * · 필드의 서명자가 이 양식에 없으면 첫 서명자로 바꾼다 — 역할은 양식 것이다.
   * · 필드가 지금 장 수보다 아래에 있으면 빈 장을 덧붙인다 — 안 그러면 저장이 막힌다.
   */
  function loadPreset(objs: LayoutObject[], presetFields: SignatureField[]) {
    const lay = layoutRef.current;
    const rid = () => crypto.randomUUID().replace(/-/g, '').slice(0, 12);
    const freshObjects = objs.map((o) => ({ ...o, id: `${o.kind.slice(0, 2)}${rid()}` }));
    const slots = new Set(roles.map((r) => r.slot));
    const fallback = roles[0]?.slot ?? null;
    const freshFields = presetFields.map((f) => {
      const signerSlot = f.signerSlot && !slots.has(f.signerSlot) ? fallback : f.signerSlot;
      const binding =
        f.binding.kind === 'signer' && !slots.has(f.binding.slot) && fallback
          ? { ...f.binding, slot: fallback }
          : f.binding;
      return { ...f, id: crypto.randomUUID(), signerSlot, binding };
    });
    const nextObjects = [...lay.objects, ...freshObjects];
    const nextFields = [...fields, ...freshFields];

    const needPages = Math.max(
      0,
      ...freshFields.map((f) => Math.floor((f.y + f.h - 1) / PAGE_H) + 1),
    );
    if (needPages > pageCount) {
      const cur = pages ?? layoutDefaultPages(pdfPageCount, nextObjects);
      setPages([
        ...cur,
        ...Array.from({ length: Math.max(0, needPages - cur.length) }, () => null),
      ]);
    }

    lay.setObjectsSilently(nextObjects);
    if (freshObjects[0]) lay.selectObject(freshObjects[0].id);
    setFields(nextFields);
    pushHistory(nextFields, nextObjects);
    lastEditSignatureRef.current = null;
    setIsDirty(true);
  }

  /** 사진을 표 칸에 넣는다 — 칸 크기에 맞춰 들어간다(맞춤 방식은 표 속성에서). */
  function putImageInCell(hit: { tableId: string; r: number; c: number }, a: AssetDragPayload) {
    setAssetUrls((m) => ({ ...m, [a.s3Key]: a.url }));
    layoutRef.current.replaceTable(hit.tableId, (t) => ({
      ...t,
      cells: t.cells.map((row, ri) =>
        row.map((cell, ci) =>
          ri === hit.r && ci === hit.c
            ? { ...cell, image: { ...(cell.image ?? {}), s3Key: a.s3Key, fileName: a.fileName } }
            : cell,
        ),
      ),
    }));
    layoutRef.current.setPlacingKind(null);
    layoutRef.current.selectObject(hit.tableId);
    layoutRef.current.setCellRange({ a: { r: hit.r, c: hit.c }, b: { r: hit.r, c: hit.c } });
    pendingAssetRef.current = null;
    setPendingAssetKey(null);
  }

  /**
   * 탐색기에서 사진 파일을 캔버스에 끌어다 놓기(여러 장 — 2026-09-23 오너 요청).
   * 모두 사진 팔레트에 올리고(자동 저장), 놓은 자리부터 계단식으로 비켜 놓는다. 한 장을 표 칸
   * 위에 놓으면 그 칸에 넣는다(Alt=자유 배치) — 팔레트에서 끌어다 놓을 때와 같은 규칙.
   * 객체는 한 번에 붙여 Ctrl+Z 한 번에 되돌아간다.
   */
  const handleFileDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    const all = Array.from(e.dataTransfer.files);
    const files = all.filter((f) => f.type === 'image/png' || f.type === 'image/jpeg');
    if (all.length > files.length) {
      toast.error(`PNG나 JPEG가 아닌 파일 ${all.length - files.length}개는 건너뛰었습니다.`);
    }
    if (files.length === 0) return;
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x0 = (e.clientX - rect.left) / zoom;
    const y0 = (e.clientY - rect.top) / zoom;
    const alt = e.altKey;
    const hit =
      !alt && files.length === 1 ? layoutHitCell(layoutRef.current.objects, x0, y0) : null;

    const placed: LayoutObject[] = [];
    const failed: string[] = [];
    for (const [i, file] of files.entries()) {
      try {
        const [asset, size] = await Promise.all([
          signatureLayoutAssetsApi.upload(file),
          readImageSize(file),
        ]);
        setAssetUrls((m) => ({ ...m, [asset.s3Key]: asset.url }));
        if (hit) {
          putImageInCell(hit, { s3Key: asset.s3Key, fileName: asset.fileName, url: asset.url });
          continue;
        }
        const box = fitImageBox(size?.w, size?.h);
        const w = Math.min(box.w, PAGE_W);
        placed.push({
          kind: 'image',
          id: 'im',
          x: Math.max(0, Math.min(PAGE_W - w, Math.round(x0 - w / 2 + i * 24))),
          y: Math.max(0, Math.round(y0 - box.h / 2 + i * 24)),
          w,
          h: box.h,
          s3Key: asset.s3Key,
          fileName: asset.fileName,
        });
      } catch (err) {
        failed.push(`${file.name}(${extractApiError(err)})`);
      }
    }
    if (placed.length > 0) layoutRef.current.appendObjects(placed);
    void queryClient.invalidateQueries({ queryKey: LAYOUT_ASSETS_QUERY_KEY });
    const ok = files.length - failed.length;
    if (ok > 0) toast.success(`사진 ${ok}장을 놓고 사진 팔레트에 저장했습니다.`);
    if (failed.length > 0) toast.error(failed.join(', '), `사진 ${failed.length}장 올리기 실패`);
  };

  /** 라이브러리에서 끌어다 놓기 — 놓은 자리를 가운데로 원본 비율 크기로 놓는다. */
  const handleAssetDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const raw = e.dataTransfer.getData(ASSET_DRAG_MIME);
    if (!raw) {
      if (Array.from(e.dataTransfer.types).includes('Files')) {
        e.preventDefault();
        void handleFileDrop(e);
      }
      return;
    }
    e.preventDefault();
    let a: AssetDragPayload;
    try {
      a = JSON.parse(raw) as AssetDragPayload;
    } catch {
      return;
    }
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;
    // 표 칸 위에 떨어뜨리면 그 칸에 넣는다. Alt 를 누르고 놓으면 표 위에 자유 배치.
    if (!e.altKey) {
      const hit = layoutHitCell(layoutRef.current.objects, x, y);
      if (hit) {
        putImageInCell(hit, a);
        return;
      }
    }
    const box = fitImageBox(a.naturalW, a.naturalH);
    const w = Math.min(box.w, PAGE_W);
    setAssetUrls((m) => ({ ...m, [a.s3Key]: a.url }));
    layoutRef.current.appendObjects([
      {
        kind: 'image',
        id: 'im',
        x: Math.max(0, Math.min(PAGE_W - w, Math.round(x - w / 2))),
        y: Math.max(0, Math.round(y - box.h / 2)),
        w,
        h: box.h,
        s3Key: a.s3Key,
        fileName: a.fileName,
      },
    ]);
    layoutRef.current.setPlacingKind(null);
    pendingAssetRef.current = null;
    setPendingAssetKey(null);
  };

  // ── Save mutation ──────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async (): Promise<UpdateSignatureTemplateResponseDto> => {
      let targetId = id!;
      let built: UpdateSignatureTemplateResponseDto | null = null;
      // 객체가 있으면(또는 있었으면) **먼저** PDF 를 다시 만든다 — 장 수가 늘어야
      // 뒤의 필드 저장이 통과한다. 서버는 바뀐 게 없으면 다시 그리지 않으므로 필드만
      // 옮긴 저장에서 버전이 갈라지지 않는다. 밑그림 키는 서버가 정한다.
      if (layout.objects.length > 0 || data?.layoutJson || pages) {
        built = await signatureTemplatesApi.buildLayout(targetId, {
          objects: layout.objects,
          basePdfS3Key: null,
          ...(pages ? { pages } : {}),
        });
        if (built.newVersionCreated) targetId = built.id;
      }
      const result = await signatureTemplatesApi.update(targetId, {
        name: templateName.trim(),
        // 표 칸이 만든 입력 칸도 같이 보낸다 — 서버가 같은 함수로 다시 만들어 맞춘다.
        fieldsJson: [...fields, ...autoFields],
        signerRolesJson: roles,
        allowAttachment,
      });
      return {
        id: result.newVersionCreated ? result.id : targetId,
        version: result.version,
        newVersionCreated: result.newVersionCreated || !!built?.newVersionCreated,
      };
    },
    onSuccess: (result) => {
      setIsDirty(false);
      // setIsDirty 는 비동기다. 아래 navigate 전에 blocker 판정과 seed effect 가
      // 참조하는 두 ref 를 동기적으로 내려둔다.
      //   - skipBlockerOnceRef: 저장이 일으킨 이동을 blocker 가 막지 않도록
      //   - isDirtyRef: 새 버전 id 의 data 가 도착했을 때 seed effect 가
      //     "미저장 편집이 있다"고 오판해 bail 하지 않도록
      isDirtyRef.current = false;
      if (result.newVersionCreated) {
        skipBlockerOnceRef.current = true;
        toast.success(`새 버전 v${result.version}을 만들었습니다.`);
        navigate(`/field/sign/templates/${result.id}/design`, { replace: true });
      } else {
        toast.success('저장되었습니다.');
        // 서버가 만든 PDF·장 수·자동 필드를 다시 받는다(isDirty 가 내려가 있어 seed 가 돈다).
        void queryClient.invalidateQueries({ queryKey: ['signature-template-detail', id] });
      }
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  // ── PDF upload ─────────────────────────────────────────────────────────
  // F1 follow-up — replaces window.prompt with auto-detect + inline modal.
  // 1) File 선택 시 detectPdfPageCount 로 자동 추정
  // 2) pendingUpload state 에 보관 → 모달 노출 → 운영자가 확인/조정
  // 3) "업로드 시작" 클릭 시 doUpload() 실행
  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !id) return;
      e.target.value = '';

      const detected = await detectPdfPageCount(file);
      const initial = detected > 0 ? detected : pageCount;
      setPendingUpload({ file, detectedPageCount: detected });
      setPendingPageCountInput(String(initial));
    },
    [id, pageCount],
  );

  const doUpload = useCallback(async () => {
    if (!pendingUpload || !id) return;
    // Clamp to 1-50 to match the modal input's max=50 attribute.
    const pc = Math.min(50, Math.max(1, parseInt(pendingPageCountInput, 10) || 1));
    const { file } = pendingUpload;
    setPendingUpload(null);
    setUploading(true);
    try {
      // §F1 hotfix — pass actual file.size so the presigned URL is
      // signed with the correct Content-Length, and forward the
      // signed headers to the S3 PUT.
      const { uploadUrl, s3Key, headers } = await signatureTemplatesApi.requestUploadUrl(
        id,
        file.name,
        file.size,
      );
      await signatureTemplatesApi.uploadPdf(uploadUrl, file, headers);
      const result = await signatureTemplatesApi.confirmUpload(id, s3Key, pc);
      // 개선계획 §5-3 — 진행 중 문서가 있는 양식의 PDF 교체는 새 버전으로 갈라진다
      // (현장 태블릿의 오프라인 PDF 캐시가 templateId:version 이라 버전을 올려야
      // 옛 PDF 가 밀려난다). 편집을 이어가려면 새 버전으로 이동해야 한다.
      if (result.newVersionCreated) {
        toast.success(`PDF를 바꾸면서 새 버전 v${result.version}을 만들었습니다.`);
        navigate(`/field/sign/templates/${result.id}/design`, { replace: true });
        return;
      }
      // 객체가 있는 양식이면 서버가 새 PDF 를 밑그림으로 삼아 객체를 다시 그렸다.
      // 화면은 밑그림을 깐다.
      const updated = await signatureTemplatesApi.get(id);
      setPdfUrl(updated.basePdfDownloadUrl || null);
      setPdfPageCount(updated.pageCount || 1);
      toast.success('PDF를 올렸습니다.');
    } catch (err) {
      // uploadPdf 가 throw 한 Error.message 가 그대로 노출되도록
      // extractApiError 의 axios fallback 외에 Error.message 도 보존.
      const message = extractApiError(err);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  }, [id, navigate, pendingPageCountInput, pendingUpload, toast]);

  const cancelPendingUpload = useCallback(() => setPendingUpload(null), []);

  // ── Overlay mousedown — begin click/drag placement ─────────────────────
  const handleOverlayMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (drag || resize) return;
      if (e.button !== 0) return; // 좌클릭만
      const rect = overlayRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Mouse coords are in screen pixels — convert to document space
      // by dividing by zoom (overlay is wrapped in a scaled container).
      const x = (e.clientX - rect.left) / zoom;
      const y = (e.clientY - rect.top) / zoom;

      // 객체 놓기 모드 — 누른 자리에 표·텍스트·도형·사진을 만든다.
      if (layout.placingKind) {
        e.preventDefault();
        // 사진을 표 칸 위에 놓으면 그 칸에 넣는다(칸 크기에 맞춰). Alt 를 누르면 자유 배치.
        const pending = pendingAssetRef.current;
        if (layout.placingKind === 'image' && pending && !e.altKey) {
          const hit = layoutHitCell(layout.objects, x, y);
          if (hit) {
            putImageInCell(hit, pending);
            return;
          }
        }
        void layout.placeAt(x, y);
        return;
      }
      if (!placingType) {
        // 빈 곳 — 누르고 끌면 선택 상자, 그냥 떼면 고른 것(객체·필드)을 모두 푼다(떼는 곳에서).
        if (e.target === overlayRef.current) {
          e.preventDefault();
          setMarquee({ sx: x, sy: y, cx: x, cy: y, moved: false, add: e.shiftKey });
        }
        return;
      }
      // 드래그 중 텍스트/이미지 선택이 잡히는 것 방지.
      e.preventDefault();
      setPlaceDrag({ type: placingType, startX: x, startY: y, curX: x, curY: y, moved: false });
    },
    [placingType, drag, resize, zoom, layout],
  );

  /**
   * 배치 확정 — 클릭이면 기억된 크기로, 드래그면 그린 크기로 필드를 만든다.
   * 두 경로 모두 타입별 최소 크기(MIN_SIZE_BY_TYPE)와 snap-to-grid 를 따른다.
   */
  const commitPlacement = useCallback(
    (p: PlaceDragState) => {
      const typeInfo = FIELD_TYPE_OPTIONS.find((t) => t.type === p.type);
      if (!typeInfo) return;

      let nx: number;
      let ny: number;
      let nw: number;
      let nh: number;

      if (p.moved) {
        // 드래그 — 그린 사각형 그대로. 어느 방향으로 끌든 좌상단이 원점이 되도록
        // min/abs 로 정규화한다. 너무 작게 그리면 타입별 최소 크기로 보정.
        nx = Math.min(p.startX, p.curX);
        ny = Math.min(p.startY, p.curY);
        nw = Math.max(minW(p.type), Math.abs(p.curX - p.startX));
        nh = Math.max(minH(p.type), Math.abs(p.curY - p.startY));
        if (snapToGrid) {
          nx = snap(nx, GRID_SIZE);
          ny = snap(ny, GRID_SIZE);
          nw = Math.max(minW(p.type), snap(nw, GRID_SIZE));
          nh = Math.max(minH(p.type), snap(nh, GRID_SIZE));
        }
        // round5 #6a 확장 — 드래그로 그린 크기도 다음 클릭 배치의 기본값이 된다.
        lastSizeByType[p.type] = { w: Math.round(nw), h: Math.round(nh) };
      } else {
        // 클릭 — round5 #6a: 같은 타입을 마지막으로 쓴 크기를 커서 중심에 놓는다.
        const remembered = lastSizeByType[p.type];
        nw = remembered?.w ?? typeInfo.defaultW;
        nh = remembered?.h ?? typeInfo.defaultH;
        nx = p.startX - nw / 2;
        ny = p.startY - nh / 2;
        if (snapToGrid) {
          nx = snap(nx, GRID_SIZE);
          ny = snap(ny, GRID_SIZE);
        }
      }

      const firstRole = roles[0] ?? null;
      const f = makeField(p.type, nx, ny, Math.round(nw), Math.round(nh), firstRole?.slot ?? null);

      const newFields = [...fields, f];
      setFields(newFields);
      pushHistory(newFields);
      setSelectedId(f.id);
      setPlacingType(null);
      setIsDirty(true);
      // 이어서 버블링될 click 이 새 필드를 선택 해제하지 못하게 막는다.
      suppressNextOverlayClickRef.current = true;
    },
    [roles, fields, pushHistory, snapToGrid],
  );

  /** 여럿 지우기 — 표가 섞여 있으면 한 번 묻는다(표 하나 지울 때와 같은 까닭). */
  const requestDeleteGroup = useCallback((ids: string[]) => {
    const lay = layoutRef.current;
    const hasTable = lay.objects.some((o) => ids.includes(o.id) && o.kind === 'table');
    if (hasTable) setConfirmDeleteGroup(ids);
    else lay.deleteObjects(ids);
  }, []);
  const requestDeleteGroupRef = useRef(requestDeleteGroup);
  requestDeleteGroupRef.current = requestDeleteGroup;

  // ── 선택 상자 ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!marquee) return;
    const docH = PAGE_H * pageCount;
    const m0 = marquee;
    function onMouseMove(e: MouseEvent) {
      const rect = overlayRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = Math.max(0, Math.min(PAGE_W, (e.clientX - rect.left) / zoom));
      const y = Math.max(0, Math.min(docH, (e.clientY - rect.top) / zoom));
      setMarquee((m) => {
        if (!m) return m;
        const moved =
          m.moved ||
          Math.abs(x - m.sx) >= PLACE_DRAG_THRESHOLD ||
          Math.abs(y - m.sy) >= PLACE_DRAG_THRESHOLD;
        return { ...m, cx: x, cy: y, moved };
      });
    }
    function onMouseUp() {
      const lay = layoutRef.current;
      setMarquee(null);
      if (!m0.moved) {
        // 빈 곳 한 번 누르기 — 고른 객체·필드를 모두 푼다(2026-09-23 오너 요청).
        lay.selectObject(null);
        setSelectedId(null);
        return;
      }
      const x1 = Math.min(m0.sx, m0.cx);
      const x2 = Math.max(m0.sx, m0.cx);
      const y1 = Math.min(m0.sy, m0.cy);
      const y2 = Math.max(m0.sy, m0.cy);
      // 상자에 **닿은** 객체 — 큰 표를 통째로 감싸지 않아도 잡히게.
      const hits = lay.objects
        .filter((o) => o.x < x2 && o.x + o.w > x1 && o.y < y2 && o.y + o.h > y1)
        .map((o) => o.id);
      const base = m0.add
        ? lay.groupIds.length > 0
          ? lay.groupIds
          : lay.selectedObjId
            ? [lay.selectedObjId]
            : []
        : [];
      lay.selectGroup(Array.from(new Set([...base, ...hits])));
      setSelectedId(null);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setMarquee(null);
    }
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [marquee, zoom, pageCount]);

  // ── Document-level placement listeners ─────────────────────────────────
  // 오버레이 밖에서 손을 떼도 배치가 완료되도록 document 에 건다. (기존
  // 이동/리사이즈 드래그와 동일한 패턴 — placeDrag 가 deps 라 클로저는 항상 최신.)
  useEffect(() => {
    if (!placeDrag) return;
    const docH = PAGE_H * pageCount;

    function onMouseMove(e: MouseEvent) {
      const rect = overlayRef.current?.getBoundingClientRect();
      if (!rect) return;
      // 페이지 밖으로 끌어도 문서 영역 안으로 가둔다.
      const x = Math.max(0, Math.min(PAGE_W, (e.clientX - rect.left) / zoom));
      const y = Math.max(0, Math.min(docH, (e.clientY - rect.top) / zoom));
      setPlaceDrag((p) => {
        if (!p) return p;
        const moved =
          p.moved ||
          Math.abs(x - p.startX) >= PLACE_DRAG_THRESHOLD ||
          Math.abs(y - p.startY) >= PLACE_DRAG_THRESHOLD;
        if (p.curX === x && p.curY === y && p.moved === moved) return p;
        return { ...p, curX: x, curY: y, moved };
      });
    }

    function onMouseUp() {
      commitPlacement(placeDrag!);
      setPlaceDrag(null);
    }

    function onKeyDown(e: KeyboardEvent) {
      // Escape — 그리던 중 취소. 필드를 만들지 않는다.
      if (e.key === 'Escape') setPlaceDrag(null);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [placeDrag, zoom, pageCount, commitPlacement]);

  // ── Field mousedown — start drag ───────────────────────────────────────
  const handleFieldMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, fieldId: string) => {
      if (placingType) return;
      e.stopPropagation();
      const field = fields.find((f) => f.id === fieldId);
      if (!field) return;
      setSelectedId(fieldId);
      setDrag({
        id: fieldId,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startFieldX: field.x,
        startFieldY: field.y,
        actuallyMoved: false,
      });
    },
    [placingType, fields],
  );

  // ── Document-level drag listeners ──────────────────────────────────────
  useEffect(() => {
    if (!drag) return;

    function onMouseMove(e: MouseEvent) {
      setDrag((d) => {
        if (!d) return d;
        // Mouse deltas are in screen pixels — divide by zoom to get
        // document-space delta so the field follows the cursor at any zoom.
        let dx = (e.clientX - d.startMouseX) / zoom;
        let dy = (e.clientY - d.startMouseY) / zoom;
        // round5 #6c — Shift 드래그 축 고정(Figma/PowerPoint 표준): 시작점
        // 기준 누적 delta 의 지배 축만 적용 — 드래그 중에도 지배 축이
        // 동적으로 바뀐다. 직선 이동을 위해 좌표를 수동 입력하던 불편 해소.
        if (e.shiftKey) {
          if (Math.abs(dx) >= Math.abs(dy)) dy = 0;
          else dx = 0;
        }
        // Skip phantom mousemoves (no actual cursor delta) so we don't
        // mark the drag as moved on a pure click.
        if (dx === 0 && dy === 0) return d;
        setFields((prev) =>
          prev.map((f) =>
            f.id === d.id
              ? {
                  ...f,
                  x: Math.max(0, Math.round(d.startFieldX + dx)),
                  y: Math.max(0, Math.round(d.startFieldY + dy)),
                  pageIndex: Math.floor(Math.max(0, d.startFieldY + dy) / PAGE_H),
                }
              : f,
          ),
        );
        return d.actuallyMoved ? d : { ...d, actuallyMoved: true };
      });
    }

    function onMouseUp() {
      // On drag-end: only apply snap + push history if the cursor actually
      // moved. A plain click on a non-grid-aligned field would otherwise
      // snap-shift the field and create a phantom history entry.
      if (!drag!.actuallyMoved) {
        setDrag(null);
        return;
      }
      setFields((prev) => {
        const next = snapToGrid
          ? prev.map((f) =>
              f.id === drag!.id
                ? {
                    ...f,
                    x: Math.max(0, snap(f.x, GRID_SIZE)),
                    y: Math.max(0, snap(f.y, GRID_SIZE)),
                    pageIndex: Math.floor(Math.max(0, snap(f.y, GRID_SIZE)) / PAGE_H),
                  }
                : f,
            )
          : prev;
        const movedField = next.find((f) => f.id === drag!.id);
        if (
          movedField &&
          (movedField.x !== drag!.startFieldX || movedField.y !== drag!.startFieldY)
        ) {
          pushHistory(next);
        }
        return next;
      });
      setDrag(null);
      setIsDirty(true);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [drag, snapToGrid, pushHistory, zoom]);

  // ── Document-level resize listeners ────────────────────────────────────
  useEffect(() => {
    if (!resize) return;

    function onMouseMove(e: MouseEvent) {
      setResize((r) => {
        if (!r) return r;
        const dx = (e.clientX - r.startMouseX) / zoom;
        const dy = (e.clientY - r.startMouseY) / zoom;
        setFields((prev) =>
          prev.map((f) => {
            if (f.id !== r.id) return f;
            let nx = r.startX;
            let ny = r.startY;
            let nw = r.startW;
            let nh = r.startH;
            switch (r.handle) {
              case 'nw':
                nx = r.startX + dx;
                ny = r.startY + dy;
                nw = r.startW - dx;
                nh = r.startH - dy;
                break;
              case 'n':
                ny = r.startY + dy;
                nh = r.startH - dy;
                break;
              case 'ne':
                ny = r.startY + dy;
                nw = r.startW + dx;
                nh = r.startH - dy;
                break;
              case 'e':
                nw = r.startW + dx;
                break;
              case 'se':
                nw = r.startW + dx;
                nh = r.startH + dy;
                break;
              case 's':
                nh = r.startH + dy;
                break;
              case 'sw':
                nx = r.startX + dx;
                nw = r.startW - dx;
                nh = r.startH + dy;
                break;
              case 'w':
                nx = r.startX + dx;
                nw = r.startW - dx;
                break;
            }
            // Clamp min sizes (acceptance-ux-changes-2026-06 §A5 — 타입별 하한)
            const mw = minW(f.type);
            const mh = minH(f.type);
            if (nw < mw) {
              if (r.handle === 'nw' || r.handle === 'w' || r.handle === 'sw') {
                nx = r.startX + r.startW - mw;
              }
              nw = mw;
            }
            if (nh < mh) {
              if (r.handle === 'nw' || r.handle === 'n' || r.handle === 'ne') {
                ny = r.startY + r.startH - mh;
              }
              nh = mh;
            }
            // Clamp position
            if (nx < 0) {
              nw += nx;
              nx = 0;
              if (nw < mw) nw = mw;
            }
            if (ny < 0) {
              nh += ny;
              ny = 0;
              if (nh < mh) nh = mh;
            }
            // Clamp against page right/bottom edges so resize can't push
            // the field beyond the canvas. Recompute min after upper clamp.
            const docH = pageCount * PAGE_H;
            nw = Math.min(nw, PAGE_W - nx);
            nh = Math.min(nh, docH - ny);
            nw = Math.max(mw, nw);
            nh = Math.max(mh, nh);

            return {
              ...f,
              x: Math.round(nx),
              y: Math.round(ny),
              w: Math.round(nw),
              h: Math.round(nh),
            };
          }),
        );
        return r;
      });
    }

    function onMouseUp() {
      setFields((prev) => {
        const next = snapToGrid
          ? prev.map((f) =>
              f.id === resize!.id
                ? {
                    ...f,
                    x: Math.max(0, snap(f.x, GRID_SIZE)),
                    y: Math.max(0, snap(f.y, GRID_SIZE)),
                    w: Math.max(minW(f.type), snap(f.w, GRID_SIZE)),
                    h: Math.max(minH(f.type), snap(f.h, GRID_SIZE)),
                  }
                : f,
            )
          : prev;
        const resizedField = next.find((f) => f.id === resize!.id);
        if (
          resizedField &&
          (resizedField.x !== resize!.startX ||
            resizedField.y !== resize!.startY ||
            resizedField.w !== resize!.startW ||
            resizedField.h !== resize!.startH)
        ) {
          pushHistory(next);
          // round5 #6a — 리사이즈 확정 크기를 타입별로 기억(다음 배치 기본값).
          lastSizeByType[resizedField.type] = { w: resizedField.w, h: resizedField.h };
        }
        return next;
      });
      setResize(null);
      setIsDirty(true);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [resize, snapToGrid, pushHistory, zoom, pageCount]);

  // ── Zoom helpers ───────────────────────────────────────────────────────
  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100));
  }, []);
  const zoomOut = useCallback(() => {
    setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100));
  }, []);
  const zoomReset = useCallback(() => setZoom(1), []);
  const zoomFit = useCallback(() => {
    // Fit-to-width: take center panel width minus padding, divide by PAGE_W.
    const el = centerPanelRef.current;
    if (!el) {
      setZoom(1);
      return;
    }
    const available = el.clientWidth - 48; // 24px padding × 2
    const fit = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, available / PAGE_W));
    setZoom(Math.round(fit * 100) / 100);
  }, []);

  // ── Ctrl+휠 확대·축소(2026-10-20 오너 요청) ─────────────────────────────
  // 캔버스 위에서만 먹고, 브라우저 화면 확대는 막는다. React onWheel 은 passive 라
  // preventDefault 가 안 돼 직접 붙인다. 커서 아래 종이 지점이 그 자리에 남도록 스크롤을 맞춘다.
  const wheelAnchorRef = useRef<{
    docX: number;
    docY: number;
    clientX: number;
    clientY: number;
  } | null>(null);
  // shownZoomRef — 화면에 그려진 배율(좌표 환산용). targetZoomRef — 휠을 빨리 굴려 다시 그리기 전에
  // 여러 번 들어와도 단계가 빠지지 않게 쌓아 가는 목표 배율.
  const shownZoomRef = useRef(zoom);
  const targetZoomRef = useRef(zoom);
  shownZoomRef.current = zoom;
  targetZoomRef.current = zoom;
  useEffect(() => {
    const el = centerPanelRef.current;
    if (!el) return;
    let acc = 0;
    function onWheel(e: WheelEvent) {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      // 마우스 휠 한 칸(≈100)은 한 단계, 트랙패드의 잘게 나뉜 값은 모아서 한 단계.
      acc += e.deltaY;
      if (Math.abs(acc) < 40) return;
      const dir = acc < 0 ? 1 : -1;
      acc = 0;
      const cur = targetZoomRef.current;
      const next = Math.max(
        ZOOM_MIN,
        Math.min(ZOOM_MAX, Math.round((cur + dir * WHEEL_ZOOM_STEP) * 100) / 100),
      );
      if (next === cur) return;
      targetZoomRef.current = next;
      const paper = el!.firstElementChild as HTMLElement | null;
      if (paper) {
        const r = paper.getBoundingClientRect();
        const shown = shownZoomRef.current;
        wheelAnchorRef.current = {
          docX: (e.clientX - r.left) / shown,
          docY: (e.clientY - r.top) / shown,
          clientX: e.clientX,
          clientY: e.clientY,
        };
      }
      setZoom(next);
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isLoading, isError]);
  useLayoutEffect(() => {
    const a = wheelAnchorRef.current;
    const el = centerPanelRef.current;
    wheelAnchorRef.current = null;
    if (!a || !el) return;
    const paper = el.firstElementChild as HTMLElement | null;
    if (!paper) return;
    const r = paper.getBoundingClientRect();
    el.scrollLeft += r.left + a.docX * zoom - a.clientX;
    el.scrollTop += r.top + a.docY * zoom - a.clientY;
  }, [zoom]);

  // ── Track current page based on scroll position ───────────────────────
  useEffect(() => {
    const el = centerPanelRef.current;
    if (!el) return;
    function onScroll() {
      if (!el) return;
      // Skip currentPage updates while a programmatic smooth-scroll is in
      // flight — otherwise the dropdown bounces through intermediate pages.
      if (isProgrammaticScrollRef.current) return;
      const scrollTop = el.scrollTop;
      const pageIdx = Math.floor((scrollTop + el.clientHeight / 2) / (PAGE_H * zoom));
      setCurrentPage(Math.min(pageCount, Math.max(1, pageIdx + 1)));
    }
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [pageCount, zoom]);

  // ── Keyboard shortcuts ─────────────────────────────────────────────────
  // Undo/Redo, Delete, Arrow nudge, Esc, Ctrl +/-/0 zoom, Ctrl+D duplicate,
  // Ctrl+C / Ctrl+V copy-paste.
  //
  // The handler reads through refs so the listener is registered ONCE
  // (empty deps). Previously the deps array included `fields`, `selectedId`,
  // and `pageCount` (via totalH) — meaning every arrow-key nudge / placement
  // tore down and re-attached the listener. During rapid key-repeat, events
  // could be missed in the gap.
  //
  // undo / redo / pushHistory / zoom* are already useCallback with empty deps
  // (they read state from historyRef etc.), but we still capture them via refs
  // for defense-in-depth so this refactor doesn't depend on their stability.
  const fieldsRef = useRef(fields);
  const selectedIdRef = useRef(selectedId);
  const pageCountRef = useRef(pageCount);
  // 붙여넣기 시 signerSlot 유효성 검사용 (복사 후 슬롯이 삭제됐을 수 있다).
  const rolesRef = useRef(roles);
  rolesRef.current = roles;
  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  const pushHistoryRef = useRef(pushHistory);
  const zoomInRef = useRef(zoomIn);
  const zoomOutRef = useRef(zoomOut);
  const zoomResetRef = useRef(zoomReset);
  // Ctrl+C 는 화면 변화가 없어 피드백이 없으면 먹었는지 알 수 없다.
  const toastRef = useRef(toast);
  toastRef.current = toast;
  // 표 칸 Ctrl+C / Ctrl+V — 규칙과 까닭은 useCellClipboard 에(2026-09-23 오너 요청).
  const cellClipNotifyRef = useRef((msg: string) => toastRef.current.info(msg));
  useCellClipboard(layoutRef, cellClipNotifyRef);
  // 표 칸 방향키 이동 — 엑셀처럼(2026-09-23 오너 요청). 규칙은 useCellArrowKeys 에.
  useCellArrowKeys(layoutRef);

  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);
  useEffect(() => {
    pageCountRef.current = pageCount;
  }, [pageCount]);
  useEffect(() => {
    undoRef.current = undo;
  }, [undo]);
  useEffect(() => {
    redoRef.current = redo;
  }, [redo]);
  useEffect(() => {
    pushHistoryRef.current = pushHistory;
  }, [pushHistory]);
  useEffect(() => {
    zoomInRef.current = zoomIn;
  }, [zoomIn]);
  useEffect(() => {
    zoomOutRef.current = zoomOut;
  }, [zoomOut]);
  useEffect(() => {
    zoomResetRef.current = zoomReset;
  }, [zoomReset]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Don't steal keystrokes from text inputs / contenteditable.
      const target = e.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }

      // 캔버스 객체 — 놓기 모드는 Esc 로 풀고, 골라진 객체는 Delete 로 지운다
      // (필드보다 먼저 본다: 객체가 골라져 있으면 그게 지금 다루는 것이다).
      const lay = layoutRef.current;
      if (lay.groupIds.length > 1) {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          requestDeleteGroupRef.current(lay.groupIds);
          return;
        }
        if (e.key === 'Escape') {
          lay.selectObject(null);
          return;
        }
      }
      if (e.key === 'Escape' && lay.placingKind) {
        lay.setPlacingKind(null);
        return;
      }
      if (lay.selectedObjId && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault();
        const sel = lay.selected;
        if (sel?.kind === 'table') {
          // 사진이 든 칸을 골라 두었으면 **사진만** 지운다(2026-09-23 오너 요청 — 종전엔 표가
          // 지워졌다). 표를 지우려면 테두리를 눌러 표 전체를 고른 뒤 Del.
          if (lay.cellRange) {
            const targets = mastersInRange(sel, lay.cellRange);
            if (targets.some((pos) => sel.cells[pos.r]?.[pos.c]?.image)) {
              lay.patchCells((c) => {
                if (!c.image) return c;
                const { image: _drop, ...rest } = c;
                return rest;
              });
              return;
            }
          }
          // 표는 한 번 묻는다(되돌릴 수 있어도 칸 설정이 많아 실수 비용이 크다).
          setConfirmDeleteTableId(lay.selectedObjId);
        } else lay.deleteObject(lay.selectedObjId);
        return;
      }

      // Snapshot the latest values from refs — never read stale closure-
      // captured versions of fields / selectedId / pageCount.
      const currentFields = fieldsRef.current;
      const currentSelectedId = selectedIdRef.current;
      const currentTotalH = pageCountRef.current * PAGE_H;

      const ctrl = e.ctrlKey || e.metaKey;

      // 표 칸을 골라 두었으면 Ctrl+C/V 는 칸 복사·붙여넣기다 — copy/paste 이벤트가 맡는다
      // (아래 필드 복사가 가로채면 칸을 골라 둔 채 필드가 붙는다).
      if (
        ctrl &&
        (e.key.toLowerCase() === 'c' || e.key.toLowerCase() === 'v') &&
        lay.selected?.kind === 'table' &&
        lay.cellRange
      ) {
        return;
      }

      // Undo (Ctrl+Z) / Redo (Ctrl+Y or Ctrl+Shift+Z)
      if (ctrl && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        undoRef.current();
        return;
      }
      if (ctrl && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) {
        e.preventDefault();
        redoRef.current();
        return;
      }

      // Zoom: Ctrl + / Ctrl - / Ctrl 0
      if (ctrl && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        zoomInRef.current();
        return;
      }
      if (ctrl && (e.key === '-' || e.key === '_')) {
        e.preventDefault();
        zoomOutRef.current();
        return;
      }
      if (ctrl && e.key === '0') {
        e.preventDefault();
        zoomResetRef.current();
        return;
      }

      // Ctrl+D — duplicate selected field
      if (ctrl && e.key.toLowerCase() === 'd' && currentSelectedId) {
        e.preventDefault();
        const original = currentFields.find((f) => f.id === currentSelectedId);
        if (!original) return;
        const duplicate = offsetDuplicate(original, currentTotalH, 1);
        const newFields = [...currentFields, duplicate];
        setFields(newFields);
        pushHistoryRef.current(newFields);
        setSelectedId(duplicate.id);
        setIsDirty(true);
        return;
      }

      // Ctrl+C — 선택 필드를 앱 내부 클립보드에 담는다 (문서 변경 없음).
      if (ctrl && e.key.toLowerCase() === 'c' && currentSelectedId) {
        const original = currentFields.find((f) => f.id === currentSelectedId);
        if (!original) return;
        e.preventDefault();
        clipboardRef.current = { ...original };
        pasteSeqRef.current = 0;
        const label =
          FIELD_TYPE_OPTIONS.find((t) => t.type === original.type)?.label ?? original.type;
        toastRef.current.info(`'${label}' 필드를 복사했습니다. Ctrl+V로 붙여 넣을 수 있습니다.`);
        return;
      }

      // Ctrl+V — 클립보드 필드를 붙여넣는다. 연속 실행하면 계단식으로 어긋난다.
      if (ctrl && e.key.toLowerCase() === 'v') {
        const copied = clipboardRef.current;
        if (!copied) return; // 복사한 게 없으면 브라우저 기본 동작에 맡긴다
        e.preventDefault();
        pasteSeqRef.current += 1;
        const pasted = offsetDuplicate(copied, currentTotalH, pasteSeqRef.current);

        // 복사 후 서명자 슬롯이 삭제됐을 수 있다. 없는 슬롯을 그대로 들고 가면
        // 색상이 빠지고 저장 시 참조가 깨지므로 첫 슬롯으로 되돌린다.
        const slots = rolesRef.current.map((r) => r.slot);
        if (pasted.signerSlot !== null && !slots.includes(pasted.signerSlot)) {
          pasted.signerSlot = slots[0] ?? null;
        }

        const newFields = [...currentFields, pasted];
        setFields(newFields);
        pushHistoryRef.current(newFields);
        setSelectedId(pasted.id);
        setIsDirty(true);
        return;
      }

      // Per-selection key handling
      if (currentSelectedId) {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          const newFields = currentFields.filter((f) => f.id !== currentSelectedId);
          setFields(newFields);
          pushHistoryRef.current(newFields);
          setSelectedId(null);
          setIsDirty(true);
          return;
        }
        if (
          e.key === 'ArrowUp' ||
          e.key === 'ArrowDown' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight'
        ) {
          e.preventDefault();
          const step = e.shiftKey ? 10 : 1;
          const dx = e.key === 'ArrowRight' ? step : e.key === 'ArrowLeft' ? -step : 0;
          const dy = e.key === 'ArrowDown' ? step : e.key === 'ArrowUp' ? -step : 0;
          const docH = currentTotalH;
          const newFields = currentFields.map((f) => {
            if (f.id !== currentSelectedId) return f;
            const nx = Math.min(PAGE_W - f.w, Math.max(0, f.x + dx));
            const ny = Math.min(docH - f.h, Math.max(0, f.y + dy));
            return {
              ...f,
              x: nx,
              y: ny,
              pageIndex: Math.floor(ny / PAGE_H),
            };
          });
          setFields(newFields);
          // F1 — Coalesce held-arrow-key nudges (key repeats fire ~30/sec) into
          // a single history entry. Same coalescing window as panel edits, but
          // tagged with kind='nudge' so panel edits and nudges never merge.
          const now = Date.now();
          const sig = lastEditSignatureRef.current;
          const shouldCoalesce =
            sig !== null &&
            sig.id === currentSelectedId &&
            sig.kind === 'nudge' &&
            sig.historyIdx === historyRef.current.idx &&
            now - sig.timestamp < EDIT_COALESCE_MS;
          if (shouldCoalesce) {
            replaceLastHistory(newFields);
          } else {
            pushHistoryRef.current(newFields);
          }
          lastEditSignatureRef.current = {
            id: currentSelectedId,
            kind: 'nudge',
            timestamp: now,
            historyIdx: historyRef.current.idx,
          };
          setIsDirty(true);
          return;
        }
        if (e.key === 'Escape') {
          setSelectedId(null);
          return;
        }
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // Empty deps — listener registered once for the lifetime of the page;
    // mutable values are read through refs above, so exhaustive-deps has
    // nothing to flag.
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────
  const selectedField = fields.find((f) => f.id === selectedId) ?? null;
  const totalH = pageCount * PAGE_H;

  // ── Render ─────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}
      >
        <span style={{ color: 'var(--text-secondary)' }}>불러오는 중…</span>
      </div>
    );
  }
  if (isError) {
    return <div style={{ padding: 32, color: 'var(--color-error)' }}>{extractApiError(error)}</div>;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        // 부모(AppShell main, 위 머리줄·안쪽 여백 뺀 높이)에 꼭 맞춘다. 100vh 면 그만큼(≈80px)
        // 화면 아래로 넘쳐 종이 칸의 가로 스크롤바가 화면 밖에 있었다(2026-10-20 오너: "좌우로
        // 창을 못 움직이네").
        height: '100%',
        overflow: 'hidden',
        background: 'var(--bg-default)',
      }}
    >
      {/* ── Top bar ────────────────────────────────────────────────────── */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 16px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'var(--bg-surface)',
          flexShrink: 0,
          height: 52,
        }}
      >
        <button
          onClick={() => navigate('/field/sign/templates')}
          title="목록으로"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 20,
            color: 'var(--fg-muted)',
            padding: '2px 6px',
            borderRadius: 'var(--r-btn)',
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <Icon name="chev-left" size={20} aria-hidden />
        </button>

        <input
          value={templateName}
          onChange={(e) => {
            setTemplateName(e.target.value);
            setIsDirty(true);
          }}
          style={{
            fontSize: 15,
            fontWeight: 600,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            minWidth: 200,
            color: 'var(--fg-default)',
          }}
          maxLength={120}
        />

        {data && (
          <span
            style={{
              fontSize: 11,
              color: 'var(--text-secondary)',
              background: 'var(--bg-muted)',
              padding: '2px 8px',
              borderRadius: 'var(--r-pill)',
              flexShrink: 0,
              fontFamily: 'var(--font-mono)',
            }}
          >
            v{data.version}
          </span>
        )}

        {/* §A6 / D2 — 겹침 경고(인라인). 저장은 막지 않음. */}
        {overlappingIds.size > 0 && (
          <span
            data-testid="overlap-warning"
            title="필드가 서로 겹쳐 있습니다. 위치 또는 크기를 조정하세요."
            style={{
              fontSize: 11,
              color: 'var(--fg-warning, #b45309)',
              background: 'var(--bg-warning-subtle, #fef3c7)',
              border: '1px solid var(--fg-warning, #f59e0b)',
              padding: '2px 8px',
              borderRadius: 'var(--r-pill)',
              flexShrink: 0,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Icon name="alert" size={12} aria-hidden /> 필드 {overlappingIds.size}개 겹침
          </span>
        )}

        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          {placingType && (
            <span
              style={{
                fontSize: 12,
                color: 'var(--blue-600)',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Icon name="map-pin" size={14} aria-hidden /> PDF 에서 클릭하여 배치
              <button
                onClick={() => setPlacingType(null)}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: 'var(--fg-muted)',
                  fontSize: 14,
                  padding: '0 4px',
                  lineHeight: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <Icon name="x" size={14} aria-hidden />
              </button>
            </span>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <input
            ref={xlsxInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            data-testid="xlsx-file-input"
            onChange={handleXlsxChange}
          />

          {/* Undo / Redo */}
          <div style={{ display: 'flex', gap: 4 }}>
            <Button
              variant="secondary"
              onClick={undo}
              disabled={!canUndo}
              title="실행 취소 (Ctrl+Z)"
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Icon name="undo" size={14} aria-hidden /> 실행 취소
              </span>
            </Button>
            <Button
              variant="secondary"
              onClick={redo}
              disabled={!canRedo}
              title="다시 실행 (Ctrl+Y)"
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Icon name="redo" size={14} aria-hidden /> 다시 실행
              </span>
            </Button>
          </div>

          {/* Snap-to-grid toggle */}
          <Button
            variant={snapToGrid ? 'primary' : 'secondary'}
            onClick={() => setSnapToGrid((v) => !v)}
            title="8px 그리드에 맞춰 정렬"
          >
            그리드 (8px)
          </Button>

          {/* Page jump dropdown — multi-page PDFs only */}
          {pageCount > 1 && (
            <select
              value={currentPage}
              onChange={(e) => {
                const target = Number(e.target.value);
                const el = centerPanelRef.current;
                if (el) {
                  // Suppress scroll handler so the dropdown doesn't bounce
                  // through intermediate pages mid-animation. Clear after
                  // ~600ms (smooth scroll typically completes within ~500ms).
                  isProgrammaticScrollRef.current = true;
                  if (programmaticScrollTimerRef.current) {
                    clearTimeout(programmaticScrollTimerRef.current);
                  }
                  el.scrollTo({ top: (target - 1) * PAGE_H * zoom, behavior: 'smooth' });
                  programmaticScrollTimerRef.current = setTimeout(() => {
                    isProgrammaticScrollRef.current = false;
                  }, 600);
                }
                setCurrentPage(target);
              }}
              style={{
                fontSize: 12,
                padding: '4px 8px',
                borderRadius: 'var(--r-input)',
                border: '1px solid var(--border-default)',
                background: 'var(--bg-surface)',
                color: 'var(--fg-default)',
                cursor: 'pointer',
              }}
              title="페이지 이동"
            >
              {Array.from({ length: pageCount }, (_, i) => (
                <option key={i} value={i + 1}>
                  페이지 {i + 1} / {pageCount}
                </option>
              ))}
            </select>
          )}

          {/* Zoom controls */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-input)',
              padding: '0 4px',
              height: 30,
            }}
          >
            <button
              onClick={zoomOut}
              disabled={zoom <= ZOOM_MIN}
              title="축소 (Ctrl+-)"
              style={{
                border: 'none',
                background: 'transparent',
                cursor: zoom <= ZOOM_MIN ? 'not-allowed' : 'pointer',
                opacity: zoom <= ZOOM_MIN ? 0.4 : 1,
                fontSize: 14,
                padding: '0 6px',
                color: 'var(--fg-default)',
              }}
            >
              −
            </button>
            <span
              onClick={zoomReset}
              title="100%로 재설정 (Ctrl+0)"
              style={{
                fontSize: 12,
                minWidth: 44,
                textAlign: 'center',
                cursor: 'pointer',
                color: 'var(--fg-default)',
                userSelect: 'none',
              }}
            >
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={zoomIn}
              disabled={zoom >= ZOOM_MAX}
              title="확대 (Ctrl++)"
              style={{
                border: 'none',
                background: 'transparent',
                cursor: zoom >= ZOOM_MAX ? 'not-allowed' : 'pointer',
                opacity: zoom >= ZOOM_MAX ? 0.4 : 1,
                fontSize: 14,
                padding: '0 6px',
                color: 'var(--fg-default)',
              }}
            >
              +
            </button>
            <button
              onClick={zoomFit}
              title="화면 너비에 맞춤"
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 11,
                padding: '0 6px',
                borderLeft: '1px solid var(--border-subtle)',
                marginLeft: 2,
                color: 'var(--fg-default)',
              }}
            >
              맞춤
            </button>
          </div>

          {/* 2026-09-22 — 빈 장을 맨 뒤에 하나 더. 장 사이에 넣거나 지우는 것은 각 장의 도구로. */}
          <Button
            variant="secondary"
            onClick={() => insertBlankPage(pageCount)}
            data-testid="add-page"
            title="빈 장을 맨 뒤에 붙입니다. 장 사이에 넣거나 지우려면 각 장 오른쪽 위 도구를 쓰세요."
          >
            + 페이지
          </Button>

          <Button
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'PDF 업로드 중…' : 'PDF 업로드'}
          </Button>

          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!isDirty || saveMutation.isPending || !templateName.trim()}
          >
            {saveMutation.isPending ? '저장 중…' : '저장'}
          </Button>
        </div>
      </header>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left panel */}
        <LeftPanel
          roles={roles}
          placingType={placingType}
          onPlacingTypeChange={(t) => {
            setPlacingType(t);
            if (t) layout.setPlacingKind(null);
          }}
          placingObject={layout.placingKind}
          onPlacingObjectChange={(k) => {
            layout.setPlacingKind(k);
            if (k) setPlacingType(null);
          }}
          onImportXlsx={() => xlsxInputRef.current?.click()}
          onOpenPresets={() => setPresetOpen(true)}
          onRolesChange={(r) => {
            setRoles(r);
            setIsDirty(true);
          }}
          allowAttachment={allowAttachment}
          onAllowAttachmentChange={(v) => {
            setAllowAttachment(v);
            setIsDirty(true);
          }}
        />

        {/* Center panel — PDF viewer + field overlay */}
        <div
          ref={centerPanelRef}
          style={{
            flex: 1,
            overflow: 'auto',
            background: 'var(--bg-sunken)',
            display: 'flex',
            padding: 24,
          }}
          onClick={() => {
            if (!placingType) {
              setSelectedId(null);
              // 종이 밖 회색 바탕 — 객체 선택도 푼다(2026-09-23 오너 요청).
              if (!layout.placingKind) layout.selectObject(null);
            }
          }}
        >
          {
            /* Outer wrapper — sized to (PAGE_W × totalH) × zoom so the
               scrollable area matches the visual size at any zoom. */
            <div
              style={{
                width: PAGE_W * zoom,
                height: totalH * zoom,
                flexShrink: 0,
                // 가운데 정렬은 margin auto 로 — justifyContent:center 는 종이가 패널보다 넓으면
                // 왼쪽 넘친 부분이 스크롤 밖으로 잘려 좌우로 못 움직였다(2026-10-20 오너 지적).
                // auto 여백은 넓어지면 0 이 되어 왼쪽 끝부터 스크롤된다.
                margin: '0 auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Inner — kept at document size (PAGE_W × totalH) but
                  scaled visually via CSS transform. Field coordinates
                  remain in document space, unchanged. */}
              <div
                style={{
                  position: 'relative',
                  width: PAGE_W,
                  height: totalH,
                  background: 'var(--bg-surface)',
                  boxShadow: 'var(--shadow-md)',
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top left',
                }}
              >
                {/* Optional 8px grid overlay (snap-to-grid hint). Rendered
                  ABOVE the PDF canvas (zIndex 8) but BELOW the field
                  overlay (zIndex 10) — purely visual, never intercepts
                  pointer events. */}
                {snapToGrid && (
                  <div
                    className="kuun-dot-grid"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      zIndex: 8,
                      backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
                    }}
                  />
                )}
                {/* 2026-09-22 — PDF 가 없어도 빈 종이를 보여 준다. 객체를 바로 놓을 수 있게. */}
                {!pdfUrl && layout.objects.length === 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 120,
                      left: 0,
                      right: 0,
                      textAlign: 'center',
                      color: 'var(--text-secondary)',
                      fontSize: 13,
                      lineHeight: 1.8,
                      pointerEvents: 'none',
                      zIndex: 5,
                    }}
                  >
                    빈 종이입니다.
                    <br />
                    왼쪽 팔레트에서 표·사진·텍스트·도형을 골라 여기를 누르면 놓입니다.
                    <br />
                    이미 만든 PDF 가 있으면 [PDF 업로드]로 밑그림을 깔 수도 있습니다.
                  </div>
                )}
                {/* PDF canvas — pdfjs-dist 로 정확한 픽셀 크기로 렌더.
                  native PDF viewer 의 toolbar/zoom 없이 디자이너 UI 처럼 표시. */}
                {pdfUrl && (
                  <PdfCanvasViewer
                    url={pdfUrl}
                    pageWidth={PAGE_W}
                    pageHeight={PAGE_H}
                    pageMap={pages ?? undefined}
                    // 바깥 컨테이너의 transform: scale(zoom) 을 알려줘 렌더 해상도를
                    // 실제 화면 기기 픽셀과 1:1 로 맞춘다 (확대해도 선명, 축소해도
                    // 다운샘플링 자글거림 없음).
                    displayScale={zoom}
                    onPageCount={(detected) => {
                      // PDF 의 실제 페이지 수가 운영자가 입력한 값과 다르면 자동 동기화.
                      // (server-side pageCount 는 confirm 시점에 운영자 입력이 들어갔기에
                      //  PDF 와 mismatch 가능 — 여기서 client-side 표시만 보정.)
                      if (detected > 0 && detected !== pdfPageCount) {
                        setPdfPageCount(detected);
                      }
                    }}
                  />
                )}

                {/* Page dividers (for multi-page PDFs) */}
                {pageCount > 1 &&
                  Array.from({ length: pageCount - 1 }, (_, i) => (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: (i + 1) * PAGE_H,
                        height: 2,
                        background: 'var(--border-strong)',
                        zIndex: 5,
                        pointerEvents: 'none',
                      }}
                    />
                  ))}

                {/* 장 도구 — 각 장 오른쪽 위: 이 앞에 빈 장 넣기 · 이 장 지우기 (2026-09-22) */}
                {Array.from({ length: pageCount }, (_, i) => (
                  <div
                    key={`page-tools-${i}`}
                    data-testid={`page-tools-${i}`}
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: i * PAGE_H + 8,
                      display: 'flex',
                      gap: 4,
                      zIndex: 40,
                    }}
                  >
                    <span
                      style={{ fontSize: 10, color: 'var(--text-secondary)', alignSelf: 'center' }}
                    >
                      {i + 1}쪽
                    </span>
                    <button
                      type="button"
                      data-testid={`page-insert-${i}`}
                      title="이 앞에 빈 장 넣기"
                      aria-label={`${i + 1}쪽 앞에 빈 장 넣기`}
                      onClick={() => insertBlankPage(i)}
                      style={pageBtnSt}
                    >
                      ＋
                    </button>
                    <button
                      type="button"
                      data-testid={`page-delete-${i}`}
                      title="이 장과 그 위의 객체, 필드를 함께 지웁니다"
                      aria-label={`${i + 1}쪽 지우기`}
                      disabled={pageCount <= 1}
                      onClick={() => deletePage(i)}
                      style={{
                        ...pageBtnSt,
                        color: 'var(--fg-danger)',
                        opacity: pageCount <= 1 ? 0.4 : 1,
                      }}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </div>
                ))}

                {/* Field overlay — captures click/drag for placement */}
                <div
                  ref={overlayRef}
                  onMouseDown={handleOverlayMouseDown}
                  onDragOver={(e) => {
                    const types = Array.from(e.dataTransfer.types);
                    if (types.includes(ASSET_DRAG_MIME) || types.includes('Files')) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'copy';
                    }
                  }}
                  onDrop={handleAssetDrop}
                  onClick={(e) => {
                    // 배치 직후의 click 한 번만 삼킨다 (선택 해제 방지).
                    if (suppressNextOverlayClickRef.current) {
                      suppressNextOverlayClickRef.current = false;
                      e.stopPropagation();
                    }
                  }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    cursor: placingType ? 'crosshair' : 'default',
                    zIndex: 10,
                  }}
                >
                  {/* 드래그 배치 미리보기 — 그리는 동안 점선 사각형 표시 */}
                  {placeDrag?.moved && (
                    <div
                      style={{
                        position: 'absolute',
                        left: Math.min(placeDrag.startX, placeDrag.curX),
                        top: Math.min(placeDrag.startY, placeDrag.curY),
                        width: Math.abs(placeDrag.curX - placeDrag.startX),
                        height: Math.abs(placeDrag.curY - placeDrag.startY),
                        // SignDocumentPrefillPanel 의 미입력 필드 표기와 동일한
                        // 시맨틱 토큰. --blue-* 는 teal 로 remap 돼 있고 다크
                        // 오버라이드도 따로 있어 hex 직접 지정은 두 테마에서 어긋난다.
                        border: '1px dashed color-mix(in srgb, var(--fg-brand) 55%, transparent)',
                        background: 'color-mix(in srgb, var(--fg-brand) 8%, transparent)',
                        pointerEvents: 'none',
                        zIndex: 30,
                      }}
                    />
                  )}
                  {marquee?.moved && (
                    <div
                      data-testid="marquee"
                      style={{
                        position: 'absolute',
                        left: Math.min(marquee.sx, marquee.cx),
                        top: Math.min(marquee.sy, marquee.cy),
                        width: Math.abs(marquee.cx - marquee.sx),
                        height: Math.abs(marquee.cy - marquee.sy),
                        border: '1px solid var(--fg-brand)',
                        background: 'color-mix(in srgb, var(--fg-brand) 10%, transparent)',
                        pointerEvents: 'none',
                        zIndex: 30,
                      }}
                    />
                  )}
                  {/* 캔버스 객체 — 필드 아래 층. 놓기 모드에서는 마우스를 먹지 않는다. */}
                  {layout.objects.map((o) => (
                    <LayoutObjectView
                      key={o.id}
                      obj={o}
                      selected={layout.selectedObjId === o.id}
                      grouped={layout.groupIds.includes(o.id)}
                      cellRange={layout.selectedObjId === o.id ? layout.cellRange : null}
                      fillRange={layout.selectedObjId === o.id ? layout.fillRange : null}
                      editing={layout.editingTextId === o.id}
                      assetUrls={assetUrls}
                      onAssetError={(k) => void refreshAssetUrls([k])}
                      passThrough={!!layout.placingKind || !!placingType}
                      onBodyMouseDown={(e) => layout.beginDrag(e, o.id)}
                      onDoubleClick={() => {
                        if (o.kind === 'text') {
                          layout.selectObject(o.id);
                          layout.setEditingTextId(o.id);
                        }
                      }}
                      onCellMouseDown={(e, r, c) => {
                        e.stopPropagation();
                        layout.pickCell(o.id, r, c, e.shiftKey);
                      }}
                      onCellMouseEnter={(r, c) => layout.hoverCell(o.id, r, c)}
                      onCellText={(r, c, text) =>
                        layout.replaceTable(
                          o.id,
                          (t) => ({
                            ...t,
                            cells: t.cells.map((row, ri) =>
                              row.map((cell, ci) =>
                                ri === r && ci === c ? { ...cell, text } : cell,
                              ),
                            ),
                          }),
                          { coalesce: true },
                        )
                      }
                      onText={(text) =>
                        layout.updateObject(o.id, { text } as Partial<LayoutObject>, {
                          coalesce: true,
                        })
                      }
                      onStopEditing={() => layout.setEditingTextId(null)}
                      onResizeMouseDown={(e, corner) => layout.beginResize(e, o.id, corner)}
                      onColResizeMouseDown={(e, c) => layout.beginColResize(e, o.id, c)}
                      onRowResizeMouseDown={(e, r) => layout.beginRowResize(e, o.id, r)}
                      onLineEndMouseDown={(e, end) => layout.beginLineEnd(e, o.id, end)}
                      onFillHandleMouseDown={(e) => layout.beginFill(e, o.id)}
                    />
                  ))}

                  {/* 표 칸이 만든 입력 칸 — 잠긴 상태로 보여만 준다. 자리는 칸이 정하고,
                      종류는 표 속성의 '칸 종류' 에서 바꾼다. */}
                  {autoFields.map((f) => {
                    const color = resolveFieldColor(f, roles);
                    const label =
                      FIELD_TYPE_OPTIONS.find((t) => t.type === f.type)?.label ?? f.type;
                    return (
                      <div
                        key={f.id}
                        data-testid="auto-field"
                        title="표 칸에서 만든 입력 칸입니다. 표 속성의 '칸 종류'에서 바꿀 수 있습니다."
                        style={{
                          position: 'absolute',
                          left: f.x,
                          top: f.y,
                          width: f.w,
                          height: f.h,
                          border: `2px dashed ${color}`,
                          background: `${color}18`,
                          borderRadius: 3,
                          pointerEvents: 'none',
                          zIndex: 11,
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'flex-end',
                          padding: 2,
                          fontSize: 9,
                          color,
                          fontWeight: 600,
                          boxSizing: 'border-box',
                        }}
                      >
                        {label}
                      </div>
                    );
                  })}

                  {fields.map((field) => {
                    const color = resolveFieldColor(field, roles);
                    const isSelected = selectedId === field.id;
                    const typeLabel =
                      FIELD_TYPE_OPTIONS.find((t) => t.type === field.type)?.label ?? field.type;
                    // 정렬 미리보기 — 텍스트성 필드는 라벨을 설정한 정렬 위치에 배치해
                    // 디자이너에서도 가운데/좌/우 정렬이 보이도록(서명/도장 등은 가운데 고정).
                    const alignable =
                      field.type === 'text' || field.type === 'date' || field.type === 'dropdown';
                    const labelJustify = alignable
                      ? field.align === 'left'
                        ? 'flex-start'
                        : field.align === 'right'
                          ? 'flex-end'
                          : 'center'
                      : 'center';

                    const handleDefs: {
                      key: ResizeHandle;
                      left: number;
                      top: number;
                      cursor: string;
                    }[] = [
                      { key: 'nw', left: field.x - 4, top: field.y - 4, cursor: 'nw-resize' },
                      {
                        key: 'n',
                        left: field.x + field.w / 2 - 4,
                        top: field.y - 4,
                        cursor: 'n-resize',
                      },
                      {
                        key: 'ne',
                        left: field.x + field.w - 4,
                        top: field.y - 4,
                        cursor: 'ne-resize',
                      },
                      {
                        key: 'e',
                        left: field.x + field.w - 4,
                        top: field.y + field.h / 2 - 4,
                        cursor: 'e-resize',
                      },
                      {
                        key: 'se',
                        left: field.x + field.w - 4,
                        top: field.y + field.h - 4,
                        cursor: 'se-resize',
                      },
                      {
                        key: 's',
                        left: field.x + field.w / 2 - 4,
                        top: field.y + field.h - 4,
                        cursor: 's-resize',
                      },
                      {
                        key: 'sw',
                        left: field.x - 4,
                        top: field.y + field.h - 4,
                        cursor: 'sw-resize',
                      },
                      {
                        key: 'w',
                        left: field.x - 4,
                        top: field.y + field.h / 2 - 4,
                        cursor: 'w-resize',
                      },
                    ];

                    return (
                      <Fragment key={field.id}>
                        <div
                          onMouseDown={(e) => handleFieldMouseDown(e, field.id)}
                          style={{
                            position: 'absolute',
                            left: field.x,
                            top: field.y,
                            width: field.w,
                            height: field.h,
                            border: `2px solid ${color}`,
                            background: `${color}22`,
                            borderRadius: 3,
                            cursor: placingType ? 'crosshair' : 'move',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: labelJustify,
                            fontSize: 10,
                            color,
                            fontWeight: 600,
                            boxShadow: isSelected
                              ? `0 0 0 3px ${color}55, 0 0 0 1px ${color}`
                              : undefined,
                            userSelect: 'none',
                            zIndex: isSelected ? 20 : 11,
                            overflow: 'hidden',
                          }}
                        >
                          {typeLabel}
                        </div>
                        {isSelected &&
                          !placingType &&
                          handleDefs.map((h) => (
                            <div
                              key={h.key}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setResize({
                                  id: field.id,
                                  handle: h.key,
                                  startMouseX: e.clientX,
                                  startMouseY: e.clientY,
                                  startX: field.x,
                                  startY: field.y,
                                  startW: field.w,
                                  startH: field.h,
                                });
                              }}
                              style={{
                                position: 'absolute',
                                left: h.left,
                                top: h.top,
                                width: 8,
                                height: 8,
                                background: 'var(--bg-surface)',
                                border: `1px solid ${color}`,
                                cursor: h.cursor,
                                zIndex: 30,
                                boxSizing: 'border-box',
                              }}
                            />
                          ))}
                      </Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          }
        </div>

        {/* 오른쪽 패널 너비 조절 — 경계를 끌어서(오너 요청 2026-09-23). 두 번 누르면 기본 너비. */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="오른쪽 패널 너비 조절"
          data-testid="right-panel-resizer"
          title="끌어서 패널 너비를 바꿉니다. 두 번 누르면 기본 너비로 돌아갑니다."
          onMouseDown={beginRightResize}
          onDoubleClick={() => saveRightW(RIGHT_PANEL_DEFAULT_W)}
          style={{
            flex: '0 0 6px',
            marginRight: -3,
            marginLeft: -3,
            cursor: 'col-resize',
            zIndex: 5,
            background: rightResizing
              ? 'color-mix(in srgb, var(--fg-brand) 35%, transparent)'
              : 'transparent',
          }}
        />

        {/* Right panel — 팔레트 '사진' 이면 사진 라이브러리, 객체가 골라져 있으면 객체 속성
            (오너 시안의 '표 속성'), 아니면 필드 속성 */}
        {layout.placingKind === 'image' ? (
          <div
            style={{
              ...panelSt,
              width: rightW,
              borderLeft: '1px solid var(--border-subtle)',
              background: 'var(--bg-surface)',
            }}
          >
            <LayoutAssetLibrary
              mode="panel"
              activeKey={pendingAssetKey}
              targetHint={
                layout.selected?.kind === 'table' && layout.cellRange
                  ? '고른 칸 크기에 맞춰 바로 들어갑니다.'
                  : undefined
              }
              onPick={(a) => {
                // 칸을 골라 둔 채 사진을 고르면 그 칸(들)에 넣는다 — 칸 크기에 맞춰진다.
                if (layout.selected?.kind === 'table' && layout.cellRange) {
                  setAssetUrls((m) => ({ ...m, [a.s3Key]: a.url }));
                  layout.patchCells((c) => ({
                    ...c,
                    image: { ...(c.image ?? {}), s3Key: a.s3Key, fileName: a.fileName },
                  }));
                  layout.setPlacingKind(null);
                  pendingAssetRef.current = null;
                  setPendingAssetKey(null);
                  toast.success(
                    '고른 칸에 사진을 넣었습니다. 맞춤·여백·불투명도는 표 속성에서 바꿉니다.',
                  );
                  return;
                }
                pendingAssetRef.current = a;
                setPendingAssetKey(a.s3Key);
              }}
              onClose={() => {
                layout.setPlacingKind(null);
                pendingAssetRef.current = null;
                setPendingAssetKey(null);
              }}
            />
          </div>
        ) : layout.groupIds.length > 1 ? (
          <div
            data-testid="group-panel"
            style={{
              ...panelSt,
              width: rightW,
              borderLeft: '1px solid var(--border-subtle)',
              background: 'var(--bg-surface)',
              padding: 16,
              gap: 10,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700 }}>
              객체 {layout.groupIds.length}개 선택됨
            </div>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              고른 객체 중 하나를 끌면 함께 옮겨집니다. Del 로 함께 지우고, Esc 나 빈 곳을 누르면
              풀립니다. Shift 를 누른 채 상자를 그리면 더 고릅니다.
            </p>
            <button
              type="button"
              data-testid="group-delete"
              onClick={() => requestDeleteGroup(layout.groupIds)}
              style={{
                padding: '7px 10px',
                borderRadius: 'var(--r-btn)',
                border: '1px solid var(--fg-danger)',
                background: 'var(--bg-surface)',
                color: 'var(--fg-danger)',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                alignSelf: 'flex-start',
              }}
            >
              {layout.groupIds.length}개 모두 삭제
            </button>
          </div>
        ) : layout.selected ? (
          <div
            style={{
              ...panelSt,
              width: rightW,
              borderLeft: '1px solid var(--border-subtle)',
              background: 'var(--bg-surface)',
            }}
          >
            <LayoutPropertyPanel
              obj={layout.selected}
              cellRange={layout.cellRange}
              roles={roles}
              onChange={(next) => layout.updateObject(next.id, () => next)}
              onDelete={() => {
                const sel = layout.selected!;
                if (sel.kind === 'table') setConfirmDeleteTableId(sel.id);
                else layout.deleteObject(sel.id);
              }}
              onCellRange={layout.setCellRange}
              onPickImage={async () => {
                const picked = await pickImage();
                return picked
                  ? ({ s3Key: picked.s3Key, fileName: picked.fileName } as LayoutImageRef)
                  : null;
              }}
            />
          </div>
        ) : (
          <RightPanel
            width={rightW}
            field={selectedField}
            roles={roles}
            onFieldChange={(updated) => {
              const newFields = fields.map((f) => (f.id === updated.id ? updated : f));
              setFields(newFields);
              // F1 — Coalesce rapid sequential edits to the SAME field into one
              // history entry. A single Undo reverts all coalesced keystrokes
              // as one logical action. We also verify the history head hasn't
              // moved since our last push (some other action like drag/resize
              // could have pushed in between) — if it has, push a fresh entry.
              const now = Date.now();
              const sig = lastEditSignatureRef.current;
              const shouldCoalesce =
                sig !== null &&
                sig.id === updated.id &&
                sig.kind === 'panel-edit' &&
                now - sig.timestamp < EDIT_COALESCE_MS &&
                sig.historyIdx === historyRef.current.idx;
              if (shouldCoalesce) {
                replaceLastHistory(newFields);
              } else {
                pushHistory(newFields);
              }
              lastEditSignatureRef.current = {
                id: updated.id,
                kind: 'panel-edit',
                timestamp: now,
                historyIdx: historyRef.current.idx,
              };
              setIsDirty(true);
            }}
            onFieldDelete={(fid) => {
              const newFields = fields.filter((f) => f.id !== fid);
              setFields(newFields);
              pushHistory(newFields);
              // F1 — A discrete action (delete) breaks coalescing.
              lastEditSignatureRef.current = null;
              setSelectedId(null);
              setIsDirty(true);
            }}
          />
        )}
      </div>

      {/* 표 삭제 확인 — Enter 로 확인, Esc 로 취소 (2026-09-23) */}
      {confirmDeleteTableId && (
        <ConfirmDialog
          message="이 표를 삭제하시겠습니까?"
          confirmLabel="삭제"
          onConfirm={() => {
            layout.deleteObject(confirmDeleteTableId);
            setConfirmDeleteTableId(null);
          }}
          onCancel={() => setConfirmDeleteTableId(null)}
        />
      )}

      {confirmDeleteGroup && (
        <ConfirmDialog
          message={`표를 포함해 고른 객체 ${confirmDeleteGroup.length}개를 삭제하시겠습니까?`}
          confirmLabel="삭제"
          onConfirm={() => {
            layout.deleteObjects(confirmDeleteGroup);
            setConfirmDeleteGroup(null);
          }}
          onCancel={() => setConfirmDeleteGroup(null)}
        />
      )}

      {/* 서식 저장/불러오기 (2026-09-22) */}
      {presetOpen && (
        <LayoutPresetDialog
          objects={layout.objects}
          fields={fields}
          onLoad={loadPreset}
          onClose={() => setPresetOpen(false)}
        />
      )}
      {/* 표 칸 사진 등 — 라이브러리를 모달로 띄워 고른다 */}
      {cellImagePick && (
        <LayoutAssetLibrary
          mode="pick"
          onPick={(a) => {
            setAssetUrls((m) => ({ ...m, [a.s3Key]: a.url }));
            cellImagePick.resolve({
              s3Key: a.s3Key,
              fileName: a.fileName,
              naturalW: a.naturalW,
              naturalH: a.naturalH,
            });
            setCellImagePick(null);
          }}
          onClose={() => {
            cellImagePick.resolve(null);
            setCellImagePick(null);
          }}
        />
      )}

      {/* F1 follow-up — page count confirm modal (replaces window.prompt) */}
      {pendingUpload && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'var(--bg-overlay)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) cancelPendingUpload();
          }}
        >
          <div
            style={{
              background: 'var(--bg-default)',
              borderRadius: 'var(--r-modal)',
              padding: 24,
              width: 380,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>PDF 업로드 확인</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
              파일:{' '}
              <span style={{ fontFamily: 'var(--font-mono)' }}>{pendingUpload.file.name}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
              {pendingUpload.detectedPageCount > 0
                ? `자동으로 찾은 페이지 수는 ${pendingUpload.detectedPageCount}입니다. 다르면 고쳐 주세요.`
                : '페이지 수를 자동 감지하지 못했습니다. 직접 입력해 주세요.'}
            </div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              페이지 수 *
            </label>
            <input
              type="number"
              min={1}
              max={50}
              value={pendingPageCountInput}
              onChange={(e) => setPendingPageCountInput(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: 'var(--r-input)',
                border: '1px solid var(--border-default)',
                fontSize: 14,
                boxSizing: 'border-box',
                marginBottom: 18,
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={cancelPendingUpload}>
                취소
              </Button>
              <Button
                onClick={doUpload}
                disabled={
                  !pendingPageCountInput.trim() ||
                  Number.isNaN(parseInt(pendingPageCountInput, 10)) ||
                  parseInt(pendingPageCountInput, 10) < 1
                }
              >
                업로드 시작
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* C-6 — Unsaved changes navigation guard */}
      {blocker.state === 'blocked' && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            background: 'var(--bg-overlay)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: 'var(--bg-default)',
              borderRadius: 'var(--r-modal)',
              padding: 24,
              width: 360,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
              저장하지 않은 변경사항
            </div>
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 20 }}>
              저장하지 않은 필드 배치가 있습니다. 이 페이지를 떠나면 변경사항이 모두 유실됩니다.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => blocker.reset?.()}>
                계속 편집
              </Button>
              <Button variant="danger" onClick={() => blocker.proceed?.()}>
                저장 없이 나가기
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
